import { useEffect, useMemo, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import RankBadge from "../../../components/club/RankBadge";
import { ClubApi } from "../../../services/clubApi";
import { type AgencyPrintTheme } from "../../../utils/agencyPrint";
import AgencyPrintOptionsModal from "../../../components/agency/AgencyPrintOptionsModal";
import {
  getDisplayableAssetRefs,
  normalizeEvidenceImageRecords,
  resolveAssetAccessMap,
} from "../../../utils/clubAssetRefs";
import { buildCasePath } from "../../../utils/clubCasePath";
import {
  buildSuspectImagePrompt,
  buildVictimProfileImagePrompt,
  extractProfileRowsFromSections,
} from "../../../utils/clubCasePrompts";
import { resolvePortraitAssignmentIndicator } from "../../../utils/clubPortraitAssignment";
import {
  CASE_VIEW_PRIMARY_READING_CLASS as EVIDENCE_PRIMARY_READING_CLASS,
  CASE_VIEW_SUPPORTING_READING_CLASS as EVIDENCE_SUPPORTING_READING_CLASS,
} from "../../../utils/caseViewTypography";
import {
  getCustomCaseViewerCopy,
  getLocalizedCustomCaseEvidenceTypeLabel,
  normalizeCustomCaseViewerLang,
  type CustomCaseViewerLang,
} from "../../../utils/customCaseViewerLocalization";
import { devLog } from "../../../utils/devLog";

const getSectionHeading = (section: any, index: number) =>
  String(section?.heading || `Section ${index + 1}`).trim() || `Section ${index + 1}`;

const getSectionBody = (section: any) =>
  String(section?.content_markdown || section?.content || "").trim();

const buildEvidenceRichTextNodes = (
  value: string,
  options: {
    boldClassName?: string;
    quoteClassName?: string;
  } = {}
) => {
  const source = String(value || "");
  const lines = source.split("\n");
  const nodes: React.ReactNode[] = [];
  const tokenPattern = /(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|("[^"]+")/g;

  lines.forEach((line, lineIndex) => {
    let cursor = 0;
    let match: RegExpExecArray | null = null;

    while ((match = tokenPattern.exec(line)) !== null) {
      if (match.index > cursor) {
        nodes.push(line.slice(cursor, match.index));
      }

      const token = match[0];
      if (token.startsWith("**") && token.endsWith("**")) {
        nodes.push(
          <strong
            key={`bold-${lineIndex}-${match.index}`}
            className={options.boldClassName || "font-semibold text-white"}
          >
            {token.slice(2, -2)}
          </strong>
        );
      } else if (token.startsWith("*") && token.endsWith("*")) {
        nodes.push(
          <strong
            key={`bold-${lineIndex}-${match.index}`}
            className={options.boldClassName || "font-semibold text-white"}
          >
            {token.slice(1, -1)}
          </strong>
        );
      } else if (token.startsWith('"') && token.endsWith('"')) {
        nodes.push(
          <span
            key={`quote-${lineIndex}-${match.index}`}
            className={options.quoteClassName || "italic text-[#d9cdb0]"}
          >
            {token}
          </span>
        );
      } else {
        nodes.push(token);
      }

      cursor = match.index + token.length;
    }

    if (cursor < line.length) {
      nodes.push(line.slice(cursor));
    }

    if (lineIndex < lines.length - 1) {
      nodes.push(<br key={`line-break-${lineIndex}`} />);
    }
  });

  return nodes;
};

const RichEvidenceText = ({
  value,
  className,
  quoteClassName,
  boldClassName,
  as: Component = "p",
}: {
  value: string;
  className: string;
  quoteClassName?: string;
  boldClassName?: string;
  as?: keyof JSX.IntrinsicElements;
}) => (
  <Component className={className}>
    {buildEvidenceRichTextNodes(value, {
      quoteClassName,
      boldClassName,
    })}
  </Component>
);

const normalizeStructuredEvidenceField = (value: string) =>
  String(value || "")
    .trim()
    .replace(/^(?:\*\*\s*)+/, "")
    .replace(/(?:\s*\*\*)+$/, "")
    .trim();

const isStructuredEvidenceLabelCandidate = (value: string) => {
  const normalized = normalizeStructuredEvidenceField(value).replace(/\s+/g, " ").trim();
  if (!normalized) return false;
  return /^[\p{L}\p{N}#&/()'’., -]{1,64}$/u.test(normalized);
};

const normalizeStructuredEvidenceAlias = (value: string) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const localizeVisibleEvidenceLabel = (value: string, panels: EvidencePanelCopy = {}) => {
  const normalized = normalizeStructuredEvidenceAlias(value);
  return panels.localizedEvidenceLabels?.[normalized] || value;
};

const buildStructuredEvidenceAliasSet = (...values: Array<string | undefined>) => {
  const aliases = new Set<string>();
  values.forEach((value) => {
    const normalized = normalizeStructuredEvidenceAlias(String(value || ""));
    if (normalized) aliases.add(normalized);
  });
  return aliases;
};

const matchStructuredEvidenceAlias = <T extends string>(
  rawLabel: string,
  aliasMap: Record<T, Set<string>>
): T | "" => {
  const normalizedLabel = normalizeStructuredEvidenceAlias(rawLabel);
  if (!normalizedLabel) return "";

  for (const [key, aliases] of Object.entries(aliasMap) as Array<[T, Set<string>]>) {
    if (aliases.has(normalizedLabel)) return key;
  }

  return "";
};

const parseStructuredEvidenceFieldLine = <T extends string>(
  line: string,
  aliasMap: Record<T, Set<string>>
): { key: T; value: string } | null => {
  const match = String(line || "").match(/^([^:]+):\s*(.+)$/);
  if (!match) return null;

  const key = matchStructuredEvidenceAlias(match[1], aliasMap);
  if (!key) return null;

  return {
    key,
    value: normalizeStructuredEvidenceField(match[2]),
  };
};

const extractLabeledRows = (sections: any[], panels: EvidencePanelCopy = {}) =>
  (Array.isArray(sections) ? sections : [])
    .flatMap((section) =>
      String(getSectionBody(section) || "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
    )
    .map((line) => {
      const match = line.match(/^([^:]+):\s*(.+)$/);
      if (!match) return null;
      if (!isStructuredEvidenceLabelCandidate(match[1])) return null;
      return {
        label: localizeVisibleEvidenceLabel(normalizeStructuredEvidenceField(match[1]), panels),
        value: normalizeStructuredEvidenceField(match[2]),
      };
    })
    .filter(Boolean) as Array<{ label: string; value: string }>;

const splitLines = (value: string) =>
  String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const isEntirelyStructuredSection = (section: any) => {
  const lines = splitLines(getSectionBody(section));
  if (!lines.length) return false;
  return extractLabeledRows([section]).length === lines.length;
};

const buildEmailFieldAliasMap = (panels: EvidencePanelCopy = {}) => ({
  from: buildStructuredEvidenceAliasSet("from", panels.from),
  to: buildStructuredEvidenceAliasSet("to", panels.to),
  cc: buildStructuredEvidenceAliasSet("cc", panels.cc),
  date: buildStructuredEvidenceAliasSet(
    "date",
    "sent",
    "envoye",
    "enviado",
    "gesendet",
    "inviata",
    panels.date
  ),
  subject: buildStructuredEvidenceAliasSet("subject", "subject line", panels.subjectLine),
  body: buildStructuredEvidenceAliasSet(
    "body",
    "message",
    "corps",
    "mensaje",
    "nachricht",
    "messaggio"
  ),
});

const buildNewspaperFieldAliasMap = (panels: EvidencePanelCopy = {}) => ({
  headline: buildStructuredEvidenceAliasSet(
    "headline",
    "title",
    "titre",
    "titular",
    "titolo",
    "uberschrift",
    "überschrift"
  ),
  byline: buildStructuredEvidenceAliasSet("byline", "signature", "firma", panels.byline),
  publication: buildStructuredEvidenceAliasSet(
    "publication",
    "newspaper",
    "paper",
    "journal",
    "giornale",
    "zeitung"
  ),
  date: buildStructuredEvidenceAliasSet("date", panels.date),
  article: buildStructuredEvidenceAliasSet(
    "article",
    "story",
    "body",
    "texte",
    "texto",
    "corps",
    "corpo",
    "artikel"
  ),
});

const buildVictimProfileFieldAliasMap = (panels: EvidencePanelCopy = {}) => ({
  name: buildStructuredEvidenceAliasSet("name", "nom", "nombre", "nome", "decedent", panels.decedent),
  age: buildStructuredEvidenceAliasSet("age", "edad", "alter", "eta", panels.age),
  sex: buildStructuredEvidenceAliasSet("sex", "sexe", "sexo", "geschlecht", "sesso", panels.sex),
  hair: buildStructuredEvidenceAliasSet(
    "hair",
    "hair color",
    "cheveux",
    "cabello",
    "haar",
    "capelli",
    panels.hair
  ),
  eyes: buildStructuredEvidenceAliasSet("eyes", "eye color", "yeux", "ojos", "augen", "occhi", panels.eyes),
  height: buildStructuredEvidenceAliasSet(
    "height",
    "taille",
    "altura",
    "hoehe",
    "statura",
    panels.height
  ),
  build: buildStructuredEvidenceAliasSet(
    "build",
    "carrure",
    "constitucion",
    "konstitution",
    "corporatura",
    panels.build
  ),
  maritalStatus: buildStructuredEvidenceAliasSet(
    "marital status",
    "etat civil",
    "estado civil",
    "familienstand",
    "stato civile",
    panels.maritalStatus
  ),
  occupation: buildStructuredEvidenceAliasSet(
    "occupation",
    "profession",
    "ocupacion",
    "beruf",
    "professione",
    panels.occupation
  ),
  employer: buildStructuredEvidenceAliasSet(
    "employer",
    "employeur",
    "empleador",
    "arbeitgeber",
    "datore di lavoro",
    panels.employer
  ),
});

const buildPoliceReportFieldAliasMap = (panels: EvidencePanelCopy = {}) => ({
  caseNumber: buildStructuredEvidenceAliasSet(
    "case number",
    "case #",
    "report number",
    "report #",
    "numero de dossier",
    "numero de rapport",
    "numero del caso",
    "numero de caso",
    "fallnummer",
    "aktenzeichen",
    "numero fascicolo",
    "numero rapporto",
    panels.caseNumber
  ),
  incidentWindow: buildStructuredEvidenceAliasSet(
    "incident window",
    "date / time",
    "date/time",
    "date time",
    "date/time reported",
    "date time reported",
    "reported at",
    "time window",
    "fenetre de l incident",
    "fecha y hora",
    "ventana del incidente",
    "zeitfenster",
    "datum uhrzeit",
    "data e ora",
    "finestra dell incidente",
    panels.incidentWindow
  ),
  bodyDiscovery: buildStructuredEvidenceAliasSet(
    "body discovery",
    "body discovered",
    "scene discovery",
    "decouverte du corps",
    "hallazgo del cuerpo",
    "auffindung des leichnams",
    "ritrovamento del corpo",
    panels.bodyDiscovery
  ),
});

const buildAutopsyFieldAliasMap = (panels: EvidencePanelCopy = {}) => ({
  caseNumber: buildStructuredEvidenceAliasSet(
    "case #",
    "case number",
    "numero de dossier",
    "numero del caso",
    "fallnummer",
    "numero fascicolo"
  ),
  name: buildStructuredEvidenceAliasSet("name", "nom", "nombre", "nome", "decedent", panels.decedent),
  age: buildStructuredEvidenceAliasSet("age", "edad", "alter", "eta", panels.age),
  sex: buildStructuredEvidenceAliasSet("sex", "sexe", "sexo", "geschlecht", "sesso", panels.sex),
});

const findStructuredRowValue = <T extends string>(
  rows: Array<{ label: string; value: string }>,
  key: T,
  aliasMap: Record<T, Set<string>>
) => rows.find((row) => matchStructuredEvidenceAlias(String(row.label || ""), aliasMap) === key)?.value || "";

const parseEmailSection = (section: any, index: number, panels: EvidencePanelCopy = {}) => {
  const heading = getSectionHeading(section, index);
  const lines = splitLines(getSectionBody(section));
  const fields = {
    from: "",
    to: "",
    cc: "",
    date: "",
    subject: "",
    body: "",
  };
  const bodyLines: string[] = [];
  let reachedBody = false;
  const aliasMap = buildEmailFieldAliasMap(panels);

  for (const line of lines) {
    const field = parseStructuredEvidenceFieldLine(line, aliasMap);
    if (!reachedBody && field && field.key !== "body") {
      fields[field.key] = field.value;
      continue;
    }
    if (!reachedBody && field?.key === "body") {
      reachedBody = true;
      bodyLines.push(field.value);
      continue;
    }
    reachedBody = true;
    bodyLines.push(line);
  }

  if (!fields.subject) fields.subject = heading;
  if (!fields.from) fields.from = "Unknown sender";
  if (!fields.to) fields.to = "Unknown recipient";
  fields.body = bodyLines.join("\n").trim() || getSectionBody(section) || "No message body.";

  return {
    ...fields,
    heading,
    key: `email-${index}`,
  };
};

const parseNewspaperSection = (section: any, index: number, panels: EvidencePanelCopy = {}) => {
  const heading = getSectionHeading(section, index);
  const lines = splitLines(getSectionBody(section));
  const fields = {
    headline: "",
    byline: "",
    publication: "",
    date: "",
    article: "",
  };
  const articleLines: string[] = [];
  let reachedArticle = false;
  const aliasMap = buildNewspaperFieldAliasMap(panels);

  for (const line of lines) {
    const field = parseStructuredEvidenceFieldLine(line, aliasMap);
    if (!reachedArticle && field) {
      fields[field.key] = field.value;
      if (field.key === "article") {
        reachedArticle = true;
      }
      continue;
    }
    reachedArticle = true;
    articleLines.push(line);
  }

  if (!fields.headline) fields.headline = heading;
  if (!fields.publication) fields.publication = "Metropolitan Daily Record";
  fields.article = [fields.article, articleLines.join("\n").trim()].filter(Boolean).join("\n\n").trim();
  if (!fields.article) fields.article = getSectionBody(section) || "No article body.";

  return {
    ...fields,
    heading,
    key: `newspaper-${index}`,
  };
};

const groupNewspaperArticles = (sections: any[], panels: EvidencePanelCopy = {}) => {
  const items = Array.isArray(sections) ? sections : [];
  if (!items.length) return [];

  const aliasMap = buildNewspaperFieldAliasMap(panels);
  const structuredHeadings = new Set(["headline", "byline", "publication", "date", "article"]);
  const hasStructuredSplitFields = items.some((section, idx) =>
    Boolean(matchStructuredEvidenceAlias(getSectionHeading(section, idx), aliasMap))
  );

  if (!hasStructuredSplitFields) {
    return items.map((section, idx) => parseNewspaperSection(section, idx, panels));
  }

  const grouped: any[] = [];
  let current: any = null;

  items.forEach((section, idx) => {
    const rawHeading = getSectionHeading(section, idx).trim();
    const normalizedHeading = matchStructuredEvidenceAlias(rawHeading, aliasMap);
    const body = getSectionBody(section);
    const inlineField = parseStructuredEvidenceFieldLine(body, aliasMap);
    const effectiveKey = structuredHeadings.has(normalizedHeading)
      ? normalizedHeading
      : inlineField?.key || "";
    const effectiveValue = inlineField ? inlineField.value : body;

    if (!effectiveKey) {
      if (current) {
        grouped.push(current);
      }
      current = parseNewspaperSection(section, grouped.length, panels);
      return;
    }

    if (!current || effectiveKey === "headline") {
      if (current) {
        grouped.push(current);
      }
      current = {
        key: `newspaper-${grouped.length}`,
        heading: rawHeading,
        headline: "",
        byline: "",
        publication: "",
        date: "",
        article: "",
      };
    }

    if (effectiveKey === "article" && current.article) {
      current.article = `${current.article}\n\n${effectiveValue}`.trim();
    } else {
      current[effectiveKey] = effectiveValue;
    }
    if (effectiveKey === "headline" && !current.heading) {
      current.heading = effectiveValue;
    }
  });

  if (current) {
    grouped.push(current);
  }

  return grouped.map((article, idx) => ({
    key: article.key || `newspaper-${idx}`,
    heading: article.heading || article.headline || `Article ${idx + 1}`,
    headline: article.headline || article.heading || `Article ${idx + 1}`,
    byline: article.byline || "",
    publication: article.publication || "Metropolitan Daily Record",
    date: article.date || "",
    article: article.article || "No article body.",
  }));
};

const parseTextSection = (section: any, index: number) => {
  const heading = getSectionHeading(section, index);
  const lines = splitLines(getSectionBody(section));
  const timestampOnlyPattern = /^\d{1,2}:\d{2}(?:\s?[ap]\.?m\.?)?$/i;
  const timestampSenderPattern = /^\d{1,2}$/;
  const messages = lines
    .filter((line) => line && !timestampOnlyPattern.test(line.trim()))
    .map((line, lineIndex) => {
    const timestampedMessageMatch = line.match(
      /^(\d{1,2}:\d{2}(?:\s?[ap]\.?m\.?)?)\s+(?:[-–]\s*)?([^:]+):\s*(.+)$/i
    );
    if (timestampedMessageMatch) {
      return {
        sender: timestampedMessageMatch[2].trim(),
        body: timestampedMessageMatch[3].trim(),
        outgoing: lineIndex % 2 === 1,
      };
    }

    const match = line.match(/^([^:]+):\s*(.+)$/);
    if (match) {
      const sender = match[1].trim();
      const body = match[2].trim();
      if (timestampSenderPattern.test(sender) && /^\d{2}(?:\s?[ap]\.?m\.?)?$/i.test(body)) {
        return null;
      }
      return {
        sender,
        body,
        outgoing: lineIndex % 2 === 1,
      };
    }

    return {
      sender: lineIndex % 2 === 0 ? "Contact" : "You",
      body: line,
      outgoing: lineIndex % 2 === 1,
    };
  })
    .filter(Boolean as any);

  const participants = Array.from(
    new Set(
      messages
        .map((message: any) => String(message?.sender || "").trim())
        .filter((sender: string) => sender && !timestampSenderPattern.test(sender))
    )
  );

  return {
    key: `text-chain-${index}`,
    heading,
    participants: participants.join(" / ") || "Conversation",
    messages: messages.filter((message: any) => message?.body),
  };
};

const normalizeLooseText = (value: string) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

const replacePreviewTokens = (
  source: string,
  context: { displayName: string; firstName: string; lastName: string; role: string }
) =>
  String(source || "")
    .replace(/\{ColdCaseClubUser\}/g, context.displayName)
    .replace(/\{role\}/gi, context.role)
    .replace(/\{first\}/gi, context.firstName)
    .replace(/\{last\}/gi, context.lastName);

const getEmbeddableVideoUrl = (value: string) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(raw);
    if (url.hostname.includes("youtube.com")) {
      const videoId = url.searchParams.get("v");
      return videoId ? `https://www.youtube.com/embed/${videoId}` : raw;
    }
    if (url.hostname.includes("youtu.be")) {
      const videoId = url.pathname.replace("/", "").trim();
      return videoId ? `https://www.youtube.com/embed/${videoId}` : raw;
    }
    if (url.hostname.includes("vimeo.com")) {
      const videoId = url.pathname.split("/").filter(Boolean).pop();
      return videoId ? `https://player.vimeo.com/video/${videoId}` : raw;
    }
    return raw;
  } catch {
    return raw;
  }
};

const isEmbeddableVideo = (value: string) => {
  const raw = String(value || "").trim().toLowerCase();
  return raw.includes("youtube.com") || raw.includes("youtu.be") || raw.includes("vimeo.com");
};

const getEvidenceTypeLabel = (value: string) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const renderEvidencePager = (options: {
  previousItem?: { title?: string; type?: string; href: string } | null;
  nextItem?: { title?: string; type?: string; href: string } | null;
  copy?: {
    previousFile?: string;
    nextFile?: string;
    previousEvidence?: string;
    nextEvidence?: string;
  };
  getTypeLabel?: (value: string) => string;
}) => {
  const resolvedCopy = {
    previousFile: "Previous File",
    nextFile: "Next File",
    previousEvidence: "Previous evidence",
    nextEvidence: "Next evidence",
    ...options.copy,
  };
  const getTypeLabel = options.getTypeLabel || getEvidenceTypeLabel;
  const pagerItems = [
    options.previousItem
      ? {
          key: `previous-${options.previousItem.href}`,
          href: options.previousItem.href,
          direction: resolvedCopy.previousFile,
          typeLabel: getTypeLabel(String(options.previousItem.type || "")),
          title:
            String(options.previousItem.title || resolvedCopy.previousEvidence).trim() ||
            resolvedCopy.previousEvidence,
          align: "items-start text-left",
        }
      : null,
    options.nextItem
      ? {
          key: `next-${options.nextItem.href}`,
          href: options.nextItem.href,
          direction: resolvedCopy.nextFile,
          typeLabel: getTypeLabel(String(options.nextItem.type || "")),
          title:
            String(options.nextItem.title || resolvedCopy.nextEvidence).trim() ||
            resolvedCopy.nextEvidence,
          align: "items-end text-right",
        }
      : null,
  ].filter(Boolean) as Array<{
    key: string;
    href: string;
    direction: string;
    typeLabel: string;
    title: string;
    align: string;
  }>;

  if (!pagerItems.length) return null;

  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2">
      {pagerItems.map((item) => (
        <Link
          key={item.key}
          to={item.href}
          className={`flex min-h-[112px] flex-col justify-between rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] px-5 py-4 transition hover:border-[#7ed0e3]/35 hover:bg-[linear-gradient(180deg,rgba(42,81,109,0.22),rgba(255,255,255,0.03))] ${item.align}`}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#d0c296]">{item.direction}</p>
            <p className="mt-2 text-sm font-semibold uppercase tracking-[0.14em] text-[#7ed0e3]">
              {item.typeLabel || "Evidence"}
            </p>
          </div>
          <p className="mt-3 text-lg font-semibold leading-tight text-white">{item.title}</p>
        </Link>
      ))}
    </div>
  );
};

const getEvidenceThumbnailUrl = (evidenceFile: any, imageAccessMap: Record<string, string>) => {
  const normalizedImages = normalizeEvidenceImageRecords(
    Array.isArray(evidenceFile?.images) ? evidenceFile.images : []
  );
  for (const image of normalizedImages) {
    const directUrl = String(image.url || "").trim();
    if (directUrl) return directUrl;
    const signedUrl = String(imageAccessMap[image.storageKey] || "").trim();
    if (signedUrl) return signedUrl;
  }
  return "";
};

const getSuspectImageRef = (suspect: any) =>
  String(
    suspect?.image_storage_key ||
      suspect?.imageStorageKey ||
      suspect?.image?.storageKey ||
      suspect?.image?.storage_key ||
      suspect?.portrait?.storageKey ||
      suspect?.portrait?.storage_key ||
      suspect?.portrait_storage_key ||
      ""
  ).trim();

const COLD_CASE_WEBSITE_URL = "https://app.coldcasegamekits.com/agency";

type PromotionModalState = {
  actorLabel: string;
  previousRankKey: string;
  previousRankLabel: string;
  currentRankKey: string;
  currentRankLabel: string;
} | null;

type SolutionEvidenceCopy = Partial<{
  finalRevealEyebrow: string;
  readyTitle: string;
  readyDescription: string;
  warningEyebrow: string;
  warningDescription: string;
  arrestWarrant: string;
  greetingCustom: (actorLabel: string) => string;
  greetingMember: (rankLabel: string, actorLabel: string) => string;
  arrestPrompt: string;
  suspectSelection: string;
  selectedSuspect: string;
  noPhoto: string;
  suspectDossier: string;
  submitting: string;
  warrantApproved: string;
  warrantRequest: string;
  warrantFiled: string;
  previewOnly: string;
  previewOnlyDescription: string;
  attempts: string;
  filedSuspect: string;
  unknownSuspect: string;
  result: string;
  correctWarrantApproved: string;
  incorrectSuspectTryAgain: string;
  correctFirstWarrant: string;
  solutionUnlockedWithoutSolveCredit: string;
  recordSummary: (count: number, nextRankLabel?: string, solvesToNextRank?: number) => string;
  customSolutionUnlocked: string;
  customRetryPrompt: string;
  guestSessionUnlocked: string;
  solutionAccess: string;
  openOfficialSolution: string;
  reveal: string;
  hide: string;
  submitCorrectPrompt: string;
  submitWarrantPrompt: string;
  shareTheWin: string;
  shareDescription: string;
  shareNow: string;
  copied: string;
  copyFailed: string;
  copyShareText: string;
  shareTextCustom: (caseTitle: string) => string;
  shareTextAgency: (caseTitle: string) => string;
  nativeShareTitle: (caseTitle: string) => string;
}>;

type EvidenceSectionHeaderCopy = Partial<{
  eyebrow: string;
  sections: (count: number) => string;
  images: (count: number) => string;
}>;

type EvidenceMediaCopy = Partial<{
  close: string;
  imageAlt: (index: number) => string;
  openLarge: string;
  signedPreviewUnavailable: string;
}>;

type EvidencePanelCopy = Partial<{
  localizedEvidenceLabels: Record<string, string>;
  photoPacket: string;
  crimeScenePhotos: string;
  imageCount: (count: number) => string;
  noCrimeScenePhotos: string;
  witnessStatements: string;
  associatedWitness: string;
  personOfInterest: string;
  statementCount: (count: number) => string;
  noAssociatedPortrait: string;
  additionalStatements: string;
  victimProfile: string;
  identitySummary: string;
  dossier: string;
  victimPortrait: string;
  portraitPendingVictim: string;
  noStructuredProfileFields: string;
  noVictimNarrative: string;
  victimProfiles: string;
  suspectDossiers: string;
  suspectCount: (count: number) => string;
  openDossier: string;
  closeDossier: string;
  portraitPendingSuspect: string;
  suspectEvidence: string;
  noSuspectEvidence: string;
  witnessMentions: string;
  witnessMentionCount: (count: number) => string;
  noSuspects: string;
  noRoleProvided: string;
  motive: string;
  age: string;
  sex: string;
  hair: string;
  eyes: string;
  height: string;
  build: string;
  maritalStatus: string;
  occupation: string;
  employer: string;
  alibi: string;
  evidenceAgainst: string;
  evidenceInFavor: string;
  opportunity: string;
  behaviorNotes: string;
  noTextChainsAvailable: string;
  recoveredTextChain: (index: number) => string;
  messageChains: string;
  messageCount: (count: number) => string;
  chainLabel: (index: number) => string;
  recoveredEmail: (index: number) => string;
  subjectLine: string;
  universityMailArchive: string;
  emailThread: string;
  from: string;
  to: string;
  cc: string;
  date: string;
  recoveredFromArchiveExport: string;
  archivedPress: string;
  newspaperCoverage: string;
  pressClip: string;
  byline: string;
  staffReporter: string;
  undatedClipping: string;
  archiveReproduction: string;
  ottawaPoliceService: string;
  majorCrimesDivision: string;
  incidentReport: string;
  filedDocumentSummary: string;
  internalUse: string;
  fileLabel: (caseNumber: string) => string;
  caseNumber: string;
  pendingAssignment: string;
  incidentWindow: string;
  seeReportBody: string;
  bodyDiscovery: string;
  officeChiefForensicPathologist: string;
  forensicPathologyUnit: string;
  postMortemExaminationReport: string;
  autopsySummary: string;
  medicalExaminerCopy: string;
  caseLabel: (caseNumber: string) => string;
  decedent: string;
  notListed: string;
  caseFile: string;
  pending: string;
  sectionLabel: (index: number) => string;
}>;

type CasePreviewChromeCopy = {
  loadingPreview: string;
  draftNotFound: string;
  backToDashboard: string;
  backToAdminCases: string;
  backToReview: string;
  editContent: string;
  editEvidence: string;
  convertToAgencyCase: string;
  convertCustomCase: string;
  editReview: string;
  cleanFinal: string;
  draftLabel: string;
  clueTags: string;
  evidenceFileView: string;
  finalEvidenceFile: string;
  agencyEvidenceFile: string;
};

const CASE_PREVIEW_CHROME_COPY: Record<CustomCaseViewerLang, CasePreviewChromeCopy> = {
  en: {
    loadingPreview: "Loading preview...",
    draftNotFound: "Draft not found",
    backToDashboard: "Back to Dashboard",
    backToAdminCases: "Back to Admin Cases",
    backToReview: "Back to Review",
    editContent: "Edit Content",
    editEvidence: "Edit Evidence",
    convertToAgencyCase: "Convert To Agency Case",
    convertCustomCase: "Convert Custom Case",
    editReview: "Edit Review",
    cleanFinal: "Clean Final",
    draftLabel: "Draft",
    clueTags: "Clue Tags",
    evidenceFileView: "Evidence File View",
    finalEvidenceFile: "Final Evidence File",
    agencyEvidenceFile: "Agency Evidence File",
  },
  fr: {
    loadingPreview: "Chargement de l'aperçu...",
    draftNotFound: "Brouillon introuvable",
    backToDashboard: "Retour au tableau de bord",
    backToAdminCases: "Retour aux affaires admin",
    backToReview: "Retour à la revue",
    editContent: "Modifier le contenu",
    editEvidence: "Modifier la preuve",
    convertToAgencyCase: "Convertir en affaire Agency",
    convertCustomCase: "Convertir l'affaire personnalisée",
    editReview: "Modifier la revue",
    cleanFinal: "Version finale",
    draftLabel: "Brouillon",
    clueTags: "Étiquettes d'indice",
    evidenceFileView: "Vue du fichier de preuve",
    finalEvidenceFile: "Fichier de preuve final",
    agencyEvidenceFile: "Fichier de preuve Agency",
  },
  es: {
    loadingPreview: "Cargando vista previa...",
    draftNotFound: "No se encontró el borrador",
    backToDashboard: "Volver al panel",
    backToAdminCases: "Volver a los casos admin",
    backToReview: "Volver a la revisión",
    editContent: "Editar contenido",
    editEvidence: "Editar prueba",
    convertToAgencyCase: "Convertir en caso Agency",
    convertCustomCase: "Convertir el caso personalizado",
    editReview: "Editar revisión",
    cleanFinal: "Versión final",
    draftLabel: "Borrador",
    clueTags: "Etiquetas de pista",
    evidenceFileView: "Vista del archivo de prueba",
    finalEvidenceFile: "Archivo de prueba final",
    agencyEvidenceFile: "Archivo de prueba Agency",
  },
  de: {
    loadingPreview: "Vorschau wird geladen...",
    draftNotFound: "Entwurf nicht gefunden",
    backToDashboard: "Zurück zum Dashboard",
    backToAdminCases: "Zurück zu den Admin-Fällen",
    backToReview: "Zurück zur Prüfung",
    editContent: "Inhalt bearbeiten",
    editEvidence: "Beweis bearbeiten",
    convertToAgencyCase: "In Agency-Fall umwandeln",
    convertCustomCase: "Individuellen Fall umwandeln",
    editReview: "Prüfung bearbeiten",
    cleanFinal: "Saubere Finalversion",
    draftLabel: "Entwurf",
    clueTags: "Spur-Tags",
    evidenceFileView: "Beweisdatei-Ansicht",
    finalEvidenceFile: "Finale Beweisdatei",
    agencyEvidenceFile: "Agency-Beweisdatei",
  },
  it: {
    loadingPreview: "Caricamento anteprima...",
    draftNotFound: "Bozza non trovata",
    backToDashboard: "Torna alla dashboard",
    backToAdminCases: "Torna ai casi admin",
    backToReview: "Torna alla revisione",
    editContent: "Modifica contenuto",
    editEvidence: "Modifica prova",
    convertToAgencyCase: "Converti in caso Agency",
    convertCustomCase: "Converti il caso personalizzato",
    editReview: "Modifica revisione",
    cleanFinal: "Versione finale",
    draftLabel: "Bozza",
    clueTags: "Tag degli indizi",
    evidenceFileView: "Vista del file di prova",
    finalEvidenceFile: "File di prova finale",
    agencyEvidenceFile: "File di prova Agency",
  },
};

export const resolveCasePreviewChromeCopy = ({
  lang,
  isStorefrontCustomSource = false,
}: {
  lang?: CustomCaseViewerLang | null;
  isStorefrontCustomSource?: boolean;
}) => {
  if (!isStorefrontCustomSource || !lang) {
    return CASE_PREVIEW_CHROME_COPY.en;
  }

  return CASE_PREVIEW_CHROME_COPY[lang] || CASE_PREVIEW_CHROME_COPY.en;
};

export const resolveMemberRankPromotion = ({
  response,
  actorLabel,
}: {
  response: any;
  actorLabel: string;
}): {
  didPromote: boolean;
  promotion: PromotionModalState;
  diagnostics: {
    backendPromoted: boolean;
    isCorrect: boolean;
    previousRankKey: string;
    currentRankKey: string;
  };
} => {
  const previousRankKey = String(response?.previousRank || "").trim();
  const currentRankKey = String(
    response?.currentRank || response?.solveStatus?.currentRankKey || ""
  ).trim();
  const isCorrect = Boolean(response?.warrant?.isCorrect);
  const backendPromoted = Boolean(response?.promoted);
  const didPromote =
    isCorrect && Boolean(previousRankKey) && Boolean(currentRankKey) && previousRankKey !== currentRankKey;

  return {
    didPromote,
    promotion: didPromote
      ? {
          actorLabel,
          previousRankKey,
          previousRankLabel:
            String(response?.previousRankLabel || "").trim() || previousRankKey,
          currentRankKey,
          currentRankLabel:
            String(response?.currentRankLabel || "").trim() ||
            String(response?.solveStatus?.currentRankLabel || "").trim() ||
            currentRankKey,
        }
      : null,
    diagnostics: {
      backendPromoted,
      isCorrect,
      previousRankKey,
      currentRankKey,
    },
  };
};

export const buildMemberWarrantFeedback = ({
  response,
  promotion,
}: {
  response: any;
  promotion: PromotionModalState;
}) => {
  if (response?.created === false) {
    return response?.warrant?.isCorrect
      ? "This warrant was already on file. The existing correct-solve record and unlocked solution have been restored."
      : "This warrant was already on file. The existing solution state has been restored.";
  }

  if (promotion?.currentRankLabel) {
    return `Warrant approved. Congratulations, you have been promoted to ${promotion.currentRankLabel} and the solution is now unlocked.`;
  }

  if (response?.warrant?.isCorrect) {
    return `Warrant approved. ${response?.solveStatus?.currentRankLabel || "Your detective record"} has been updated and the solution is now unlocked.`;
  }

  return "Warrant filed. The solution is now unlocked, but this case did not count as a successful solve.";
};

const RankPromotionModal = ({
  promotion,
  onClose,
}: {
  promotion: PromotionModalState;
  onClose: () => void;
}) =>
  promotion ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(3,6,12,0.84)] px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rank-promotion-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-[32px] border border-amber-200/18 bg-[radial-gradient(circle_at_top,rgba(217,119,6,0.16),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(34,211,238,0.14),transparent_34%),linear-gradient(180deg,rgba(13,18,28,0.98),rgba(9,12,20,0.98))] shadow-[0_36px_120px_rgba(0,0,0,0.55)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b border-white/10 px-5 py-5 sm:px-7 sm:py-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-amber-100/80">Rank Advancement</p>
              <h2 id="rank-promotion-title" className="mt-2 text-3xl font-black text-white sm:text-4xl">
                Congratulations, Detective {promotion.actorLabel}.
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-7 text-white/78 sm:text-base">
                Your correct warrant request has been confirmed. Your superior detective work has advanced your standing inside the Cold Case Agency.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white/80 transition hover:bg-white/[0.08]"
            >
              Close
            </button>
          </div>
        </div>

        <div className="grid gap-4 px-5 py-5 sm:grid-cols-2 sm:px-7 sm:py-6">
          <div className="rounded-[24px] border border-white/10 bg-white/[0.03] p-5">
            <p className="text-[11px] uppercase tracking-[0.22em] text-white/52">Previous Rank</p>
            <div className="mt-4 flex items-center gap-4">
              <RankBadge
                rankKey={promotion.previousRankKey}
                rankLabel={promotion.previousRankLabel}
                size={88}
                className="h-[88px] w-[88px] rounded-[22px] border border-white/10 bg-white/5 p-2"
              />
              <p className="text-xl font-semibold text-white">{promotion.previousRankLabel}</p>
            </div>
          </div>
          <div className="rounded-[24px] border border-amber-300/28 bg-[linear-gradient(180deg,rgba(217,119,6,0.16),rgba(255,255,255,0.04))] p-5">
            <p className="text-[11px] uppercase tracking-[0.22em] text-amber-100/78">New Rank</p>
            <div className="mt-4 flex items-center gap-4">
              <RankBadge
                rankKey={promotion.currentRankKey}
                rankLabel={promotion.currentRankLabel}
                size={88}
                className="h-[88px] w-[88px] rounded-[22px] border border-amber-200/16 bg-white/5 p-2"
              />
              <div>
                <p className="text-xl font-semibold text-white">{promotion.currentRankLabel}</p>
                <p className="mt-1 text-sm text-amber-50/78">Your new badge has been added to your detective record.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 bg-black/15 px-5 py-4 sm:px-7">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-white/68">
              A promotion email has been issued to your member email with your updated rank record.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-cyan-300/30 bg-cyan-400/12 px-5 py-2.5 text-sm font-semibold text-cyan-50 transition hover:bg-cyan-400/18"
            >
              Continue To Solution
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

const renderLightbox = (
  lightbox: { url: string; alt: string } | null,
  onClose: () => void,
  copy: EvidenceMediaCopy = {}
) => {
  const resolvedCopy = {
    close: "Close",
    ...copy,
  };

  return (
  lightbox ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-h-[92vh] max-w-[92vw] overflow-hidden rounded-3xl border border-white/10 bg-zinc-950 p-3 shadow-[0_30px_90px_rgba(0,0,0,0.65)]"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-full border border-white/15 bg-black/50 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-black/70"
        >
          {resolvedCopy.close}
        </button>
        <img src={lightbox.url} alt={lightbox.alt} className="max-h-[86vh] max-w-[86vw] rounded-2xl object-contain" />
      </div>
    </div>
  ) : null
  );
};

const renderImageStrip = (
  images: Array<{ storageKey: string; url: string; description?: string }>,
  emptyLabel: string,
  options: {
    finalView?: boolean;
    onOpenImage?: (url: string, alt: string) => void;
    copy?: EvidenceMediaCopy;
  } = {}
) => (
  <div className="grid gap-2.5 md:grid-cols-2 md:gap-3">
    {images.length > 0 ? (
      images.map((image, idx) => (
        <div
          key={`${image.storageKey}-${idx}`}
          className="overflow-hidden rounded-[22px] border border-white/10 bg-white/5 sm:rounded-3xl"
        >
          {image.url ? (
            <div className="relative">
              <img
                src={image.url}
                alt={options.copy?.imageAlt?.(idx) || `Evidence image ${idx + 1}`}
                className="h-64 w-full cursor-zoom-in object-cover sm:h-72"
                onDoubleClick={() =>
                  options.onOpenImage?.(image.url, options.copy?.imageAlt?.(idx) || `Evidence image ${idx + 1}`)
                }
              />
              <button
                type="button"
                onClick={() =>
                  options.onOpenImage?.(image.url, options.copy?.imageAlt?.(idx) || `Evidence image ${idx + 1}`)
                }
                className="absolute bottom-2.5 right-2.5 rounded-full border border-white/15 bg-black/60 px-2.5 py-1.5 text-[10px] font-semibold text-white/90 hover:bg-black/75 sm:bottom-3 sm:right-3 sm:px-3 sm:text-[11px]"
              >
                {options.copy?.openLarge || "Open Large"}
              </button>
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center bg-[linear-gradient(135deg,rgba(255,255,255,0.07),rgba(255,255,255,0.02))] p-5 text-sm text-white/60 sm:h-72 sm:p-6">
              {options.copy?.signedPreviewUnavailable || "Signed preview unavailable"}
            </div>
          )}
          {options.finalView ? null : (
            <div className="border-t border-white/10 p-2.5 sm:p-3">
              <p className="break-all font-mono text-[11px] text-white/55">{image.storageKey}</p>
            </div>
          )}
          {image.description ? (
            <div className="border-t border-white/10 px-2.5 py-2.5 sm:px-3 sm:py-3">
              <p className={`text-white/80 ${EVIDENCE_SUPPORTING_READING_CLASS}`}>{image.description}</p>
            </div>
          ) : null}
        </div>
      ))
    ) : (
      <div className="rounded-[22px] border border-dashed border-white/15 bg-white/5 p-5 text-sm text-white/55 sm:rounded-3xl sm:p-6">
        {emptyLabel}
      </div>
    )}
  </div>
);

const renderGenericEvidence = (sections: any[], panels: EvidencePanelCopy = {}) => (
  <div className="space-y-2 sm:space-y-3">
    {sections.map((section, idx) => (
      <div
        key={`generic-${idx}`}
        className="rounded-[16px] border border-white/10 bg-black/20 px-2.5 py-2.5 sm:rounded-2xl sm:p-4"
      >
        <p className="text-[10px] uppercase tracking-[0.15em] text-white/45 sm:text-xs sm:tracking-wide">
          {localizeVisibleEvidenceLabel(getSectionHeading(section, idx), panels)}
        </p>
        <RichEvidenceText
          value={getSectionBody(section)}
          className={`mt-2 whitespace-pre-wrap text-white/85 sm:mt-3 ${EVIDENCE_PRIMARY_READING_CLASS}`}
        />
      </div>
    ))}
  </div>
);

const humanizeEvidenceType = (value: string) =>
  String(value || "evidence")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim() || "Evidence";

const getAgencyCaseCycleNumber = (rawCaseIndex: any) => {
  const parsedCaseIndex = Number(rawCaseIndex);
  if (!Number.isFinite(parsedCaseIndex) || parsedCaseIndex < 1001) return 1;
  return Math.max(1, parsedCaseIndex - 1000);
};

const resolveCasePreviewLocalization = (
  draftCase: any
): {
  lang: CustomCaseViewerLang;
  source: string;
  copy: ReturnType<typeof getCustomCaseViewerCopy>;
} | null => {
  const candidates: Array<{ source: string; value: unknown }> = [
    { source: "case_payload_language", value: draftCase?.casePayload?.language },
    { source: "case_payload_content_language", value: draftCase?.casePayload?.contentLanguage },
    { source: "casegen_custom_context_language", value: draftCase?.generationMeta?.casegenConfig?.customContext?.language },
    { source: "draft_language", value: draftCase?.language },
  ];

  for (const candidate of candidates) {
    const lang = normalizeCustomCaseViewerLang(candidate.value);
    if (lang) {
      return {
        lang,
        source: candidate.source,
        copy: getCustomCaseViewerCopy(lang),
      };
    }
  }

  return null;
};

const getCaseDynamicCounts = (evidenceFiles: any[], suspects: any[]) => {
  const files = Array.isArray(evidenceFiles) ? evidenceFiles : [];
  const suspectList = Array.isArray(suspects) ? suspects : [];

  return {
    suspectCount: suspectList.length,
    evidenceCount: files.length,
    imageCount: files.reduce(
      (count, file) => count + normalizeEvidenceImageRecords(Array.isArray(file?.images) ? file.images : []).length,
      0
    ),
    sectionCount: files.reduce(
      (count, file) => count + (Array.isArray(file?.sections) ? file.sections.length : 0),
      0
    ),
  };
};

const renderTimelineEvidence = (sections: any[], panels: EvidencePanelCopy = {}) => (
  <div className="space-y-2.5 sm:space-y-4">
    {sections.map((section, idx) => (
      <div key={`timeline-${idx}`} className="relative pl-5 sm:pl-7">
        <div className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-cyan-300 shadow-[0_0_0_3px_rgba(34,211,238,0.14)] sm:h-3 sm:w-3 sm:shadow-[0_0_0_4px_rgba(34,211,238,0.14)]" />
        {idx < sections.length - 1 && (
          <div className="absolute left-1 top-4.5 h-[calc(100%-6px)] w-px bg-white/10 sm:left-[5px] sm:top-5 sm:h-[calc(100%-8px)]" />
        )}
        <div className="rounded-[16px] border border-white/10 bg-black/20 p-2.5 sm:rounded-2xl sm:p-4">
          <p className="text-[10px] uppercase tracking-[0.16em] text-cyan-200/80 sm:text-xs sm:tracking-[0.18em]">
            {localizeVisibleEvidenceLabel(getSectionHeading(section, idx), panels)}
          </p>
          <RichEvidenceText
            value={getSectionBody(section)}
            className={`mt-2 whitespace-pre-wrap text-white/85 sm:mt-3 ${EVIDENCE_PRIMARY_READING_CLASS}`}
          />
        </div>
      </div>
    ))}
  </div>
);

const getSuspectMatchTokens = (suspect: any) =>
  [
    String(suspect?.name || ""),
    String(suspect?.role || ""),
    String(suspect?.relation || ""),
    String(suspect?.occupation || ""),
  ]
    .map((value) => normalizeLooseText(value))
    .filter((value) => value.length >= 4);

const getWitnessStatementGroups = (sections: any[], suspects: any[]) => {
  const normalizedSuspects = (Array.isArray(suspects) ? suspects : []).map((suspect, suspectIndex) => ({
    suspect,
    suspectIndex,
    tokens: getSuspectMatchTokens(suspect),
  }));

  const groups = normalizedSuspects.map(({ suspect, suspectIndex }) => ({
    key: `suspect-${suspectIndex}`,
    suspect,
    suspectIndex,
    statements: [] as Array<{ section: any; index: number }>,
  }));
  const unassigned: Array<{ section: any; index: number }> = [];

  (Array.isArray(sections) ? sections : []).forEach((section, index) => {
    const haystack = normalizeLooseText(`${getSectionHeading(section, index)} ${getSectionBody(section)}`);
    const match = normalizedSuspects.find(({ tokens }) => tokens.some((token) => token && haystack.includes(token)));
    if (!match) {
      unassigned.push({ section, index });
      return;
    }
    groups[match.suspectIndex]?.statements.push({ section, index });
  });

  return {
    matchedGroups: groups.filter((group) => group.statements.length > 0),
    unassigned,
  };
};

const renderWitnessEvidence = (
  sections: any[],
  suspects: any[],
  suspectImageAccessMap: Record<string, string>,
  options: {
    finalView?: boolean;
    onOpenImage?: (url: string, alt: string) => void;
    copy?: EvidenceMediaCopy;
    panels?: EvidencePanelCopy;
  } = {}
) => {
  const { matchedGroups, unassigned } = getWitnessStatementGroups(sections, suspects);
  const resolvedPanels = {
    witnessStatements: "Witness Statements",
    associatedWitness: "Associated Witness",
    personOfInterest: "Person of interest",
    statementCount: (count: number) => `${count} statement${count === 1 ? "" : "s"}`,
    noAssociatedPortrait: "No associated portrait",
    additionalStatements: "Additional Statements",
    ...options.panels,
  };

  return (
    <div className="space-y-3 sm:space-y-5">
      {matchedGroups.map((group) => {
        const suspect = group.suspect || {};
        const imageRef = getSuspectImageRef(suspect);
        const imageUrl =
          String(suspect?.image_url || suspect?.portrait_url || "").trim() ||
          suspectImageAccessMap[imageRef] ||
          "";

        return (
          <div key={group.key} className="rounded-[22px] border border-white/10 bg-black/20 p-3 sm:rounded-3xl sm:p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-amber-200/70">{resolvedPanels.witnessStatements}</p>
                <p className="mt-1 text-lg font-semibold leading-tight text-white sm:text-xl">
                  {suspect?.name || resolvedPanels.associatedWitness}
                </p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-white/45 sm:text-[11px] sm:tracking-[0.16em]">
                  {suspect?.role || suspect?.relation || resolvedPanels.personOfInterest}
                </p>
              </div>
              <span className="rounded-full border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] text-white/60 sm:px-3">
                {resolvedPanels.statementCount(group.statements.length)}
              </span>
            </div>

            <div className="mt-3 grid gap-3 lg:mt-4 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-4">
              <div>
                {imageUrl ? (
                  <div className="relative">
                    <img
                      src={imageUrl}
                      alt={suspect?.name || "Witness association"}
                      className="aspect-[4/5] w-full cursor-zoom-in rounded-[18px] border border-white/10 object-cover sm:rounded-2xl"
                      onDoubleClick={() =>
                        options.onOpenImage?.(imageUrl, suspect?.name || "Witness association")
                      }
                    />
                    <button
                      type="button"
                      onClick={() =>
                        options.onOpenImage?.(imageUrl, suspect?.name || "Witness association")
                      }
                      className="absolute bottom-2.5 right-2.5 rounded-full border border-white/15 bg-black/60 px-2.5 py-1.5 text-[10px] font-semibold text-white/90 hover:bg-black/75 sm:bottom-3 sm:right-3 sm:px-3 sm:text-[11px]"
                    >
                      {options.copy?.openLarge || "Open Large"}
                    </button>
                  </div>
                ) : (
                  <div className="flex aspect-[4/5] w-full items-center justify-center rounded-[18px] border border-dashed border-white/15 bg-white/5 p-4 text-center text-sm text-white/50 sm:rounded-2xl sm:p-6">
                    {resolvedPanels.noAssociatedPortrait}
                  </div>
                )}
              </div>

              <div className="space-y-2.5 sm:space-y-3">
                {group.statements.map(({ section, index }) => (
                  <div
                    key={`witness-${group.key}-${index}`}
                    className="rounded-[18px] border border-white/10 bg-white/5 px-3 py-3 sm:rounded-2xl sm:p-4"
                  >
                    <p className="text-[10px] uppercase tracking-[0.16em] text-amber-200/80 sm:text-xs sm:tracking-[0.18em]">
                      {getSectionHeading(section, index)}
                    </p>
                    <RichEvidenceText
                      value={getSectionBody(section)}
                      className={`mt-2.5 whitespace-pre-wrap text-white/85 sm:mt-3 ${EVIDENCE_PRIMARY_READING_CLASS}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      {unassigned.length > 0 ? (
        <div className="rounded-[22px] border border-white/10 bg-black/20 p-3 sm:rounded-3xl sm:p-4">
          <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">{resolvedPanels.additionalStatements}</p>
          <div className="mt-3 grid gap-3 lg:mt-4 lg:grid-cols-2 lg:gap-4">
            {unassigned.map(({ section, index }) => (
              <div
                key={`witness-unassigned-${index}`}
                className="rounded-[18px] border border-white/10 bg-white/5 px-3 py-3 sm:rounded-2xl sm:p-4"
              >
                <p className="text-[10px] uppercase tracking-[0.16em] text-amber-200/80 sm:text-xs sm:tracking-[0.18em]">
                  {getSectionHeading(section, index)}
                </p>
                <RichEvidenceText
                  value={getSectionBody(section)}
                  className={`mt-2.5 whitespace-pre-wrap text-white/85 sm:mt-3 ${EVIDENCE_PRIMARY_READING_CLASS}`}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const TextChainEvidence = ({ sections, panels = {} }: { sections: any[]; panels?: EvidencePanelCopy }) => {
  const chains = useMemo(() => sections.map((section, idx) => parseTextSection(section, idx)), [sections]);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeChain = chains[activeIndex] || null;
  const resolvedPanels = {
    noTextChainsAvailable: "No text chains available.",
    recoveredTextChain: (index: number) => `Recovered Text Chain ${index + 1}`,
    messageChains: "Message Chains",
    messageCount: (count: number) => `${count} msgs`,
    chainLabel: (index: number) => `Chain ${index + 1}`,
    ...panels,
  };

  useEffect(() => {
    setActiveIndex(0);
  }, [sections]);

  if (!chains.length) {
    return (
      <div className="rounded-3xl border border-dashed border-white/15 bg-white/5 p-6 text-sm text-white/55">
        {resolvedPanels.noTextChainsAvailable}
      </div>
    );
  }

  return (
    <div className="space-y-3 lg:grid lg:gap-4 lg:space-y-0 lg:grid-cols-[260px_minmax(0,1fr)]">
      <div className="space-y-2.5 lg:hidden">
        {chains.map((chain, chainIndex) => (
          <section
            key={`${chain.key}-mobile`}
            className="overflow-hidden rounded-[18px] border border-white/10 bg-[linear-gradient(180deg,#09111a,#0f1724)]"
          >
            <div className="border-b border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] px-2.5 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-cyan-200/70">
                    {resolvedPanels.recoveredTextChain(chainIndex)}
                  </p>
                  <p className="mt-1 text-[15px] font-semibold leading-tight text-white">{chain.heading}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.16em] text-white/45">{chain.participants}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="rounded-full border border-cyan-300/20 bg-cyan-400/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-100/80">
                    {resolvedPanels.messageCount(chain.messages.length)}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-1.5 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.08),transparent_35%),linear-gradient(180deg,#0a1018,#0e1725)] px-2 py-2.5">
              {chain.messages.map((message, index) => (
                <div key={`${chain.key}-mobile-message-${index}`} className={`flex ${message.outgoing ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[93%] rounded-[16px] border px-2.5 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.22)] ${
                      message.outgoing
                        ? "border-cyan-300/25 bg-cyan-400/12 text-cyan-50"
                        : "border-white/10 bg-zinc-950 text-white"
                    }`}
                  >
                    <p className={`text-[10px] uppercase tracking-[0.16em] ${message.outgoing ? "text-cyan-100/70" : "text-white/45"}`}>
                      {message.sender}
                    </p>
                    <RichEvidenceText
                      value={message.body}
                      className={`mt-1 ${EVIDENCE_SUPPORTING_READING_CLASS}`}
                      quoteClassName={message.outgoing ? "italic text-cyan-100/80" : "italic text-[#d0c9ba]"}
                      boldClassName="font-semibold text-inherit"
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <aside className="hidden rounded-3xl border border-white/10 bg-black/20 p-3 lg:block">
        <p className="px-2 pb-3 text-[10px] uppercase tracking-[0.22em] text-white/45">{resolvedPanels.messageChains}</p>
        <div className="space-y-2">
          {chains.map((chain, index) => (
            <button
              key={chain.key}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`w-full rounded-2xl border px-3 py-3 text-left transition ${
                index === activeIndex
                  ? "border-cyan-300/35 bg-cyan-400/10"
                  : "border-white/10 bg-white/5 hover:bg-white/10"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-white">{chain.heading}</p>
                <span className="rounded-full border border-white/10 bg-black/20 px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-white/55">
                  {chain.messages.length}
                </span>
              </div>
              <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-white/50">{chain.participants}</p>
            </button>
          ))}
        </div>
      </aside>

      <section className="hidden overflow-hidden rounded-3xl border border-white/10 bg-[linear-gradient(180deg,#09111a,#0f1724)] lg:block">
        <div className="border-b border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-cyan-200/70">{resolvedPanels.recoveredTextChain(activeIndex)}</p>
              <p className="mt-2 text-lg font-semibold text-white">{activeChain?.heading}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-white/45">{activeChain?.participants}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-100/80">
                {resolvedPanels.messageCount(activeChain?.messages?.length || 0)}
              </span>
              <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/60">
                {resolvedPanels.chainLabel(activeIndex)}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-3 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.08),transparent_35%),linear-gradient(180deg,#0a1018,#0e1725)] px-4 py-5">
          {activeChain?.messages.map((message, index) => (
            <div key={`${activeChain.key}-message-${index}`} className={`flex ${message.outgoing ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[88%] rounded-[22px] border px-4 py-3 shadow-[0_8px_24px_rgba(0,0,0,0.22)] ${
                  message.outgoing
                    ? "border-cyan-300/25 bg-cyan-400/12 text-cyan-50"
                    : "border-white/10 bg-zinc-950 text-white"
                }`}
              >
                <p className={`text-[10px] uppercase tracking-[0.16em] ${message.outgoing ? "text-cyan-100/70" : "text-white/45"}`}>
                  {message.sender}
                </p>
                <RichEvidenceText
                  value={message.body}
                  className={`mt-1 ${EVIDENCE_SUPPORTING_READING_CLASS}`}
                  quoteClassName={message.outgoing ? "italic text-cyan-100/80" : "italic text-[#d0c9ba]"}
                  boldClassName="font-semibold text-inherit"
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

const renderTextEvidence = (sections: any[], options: { panels?: EvidencePanelCopy } = {}) => (
  <TextChainEvidence sections={sections} panels={options.panels} />
);

const renderEmailEvidence = (sections: any[], options: { panels?: EvidencePanelCopy } = {}) => {
  const resolvedPanels = {
    recoveredEmail: (index: number) => `Recovered Email ${index + 1}`,
    subjectLine: "Subject Line",
    universityMailArchive: "University Mail Archive",
    emailThread: "Email Thread",
    from: "From",
    to: "To",
    cc: "CC",
    date: "Date",
    recoveredFromArchiveExport: "Recovered from archive export",
    ...options.panels,
  };

  return (
  <div className="space-y-3 sm:space-y-4">
    {sections.map((section, idx) => {
      const email = parseEmailSection(section, idx, options.panels);
      const metaRows = [
        [resolvedPanels.from, email.from],
        [resolvedPanels.to, email.to],
        [resolvedPanels.cc, email.cc],
        [resolvedPanels.date, email.date],
      ].filter(([, value]) => String(value || "").trim());

      return (
        <article
          key={email.key}
          className="overflow-hidden rounded-[20px] border border-slate-300/60 bg-[linear-gradient(180deg,#f7f9fc,#e9eef5)] text-slate-900 shadow-[0_18px_40px_rgba(0,0,0,0.22)] sm:rounded-3xl"
        >
          <div className="border-b border-slate-300/70 bg-[linear-gradient(180deg,#eef2f7,#dde6f0)] px-3 py-2.5 sm:px-5 sm:py-3.5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-300/90" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-300/90" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-300/90" />
              </div>
              <p className="text-[9px] uppercase tracking-[0.24em] text-slate-500 sm:text-[10px]">
                {resolvedPanels.recoveredEmail(idx)}
              </p>
            </div>
          </div>

          <div className="border-b border-slate-300/70 bg-[linear-gradient(180deg,#fdfefe,#eef3f9)] px-3 py-3 sm:px-6 sm:py-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{resolvedPanels.subjectLine}</p>
                <h3 className="mt-1.5 text-[1.35rem] font-semibold leading-[1.1] text-slate-900 sm:text-[1.55rem]">
                  {email.subject}
                </h3>
                <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                  {resolvedPanels.universityMailArchive}
                </p>
              </div>
              <span className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600 sm:px-3">
                {resolvedPanels.emailThread}
              </span>
            </div>
          </div>

          <div className="border-b border-slate-300/70 bg-white/80">
            <div className="grid gap-0 sm:grid-cols-2">
              {metaRows.map(([label, value], metaIdx) => (
                <div
                  key={`${email.key}-${label}`}
                  className={`px-3 py-2.5 sm:px-6 sm:py-3 ${
                    metaIdx > 0 ? "border-t border-slate-200/90" : ""
                  } ${metaIdx % 2 === 1 ? "sm:border-l sm:border-slate-200/90" : ""} ${
                    metaIdx > 1 ? "sm:border-t border-slate-200/90" : ""
                  }`}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
                  <RichEvidenceText
                    value={String(value)}
                    className="mt-1 text-[13px] leading-5 text-slate-800 sm:text-sm sm:leading-6"
                    quoteClassName="italic text-slate-600"
                    boldClassName="font-semibold text-slate-900"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white px-3 py-3 sm:px-6 sm:py-6">
            <RichEvidenceText
              value={email.body}
              className={`whitespace-pre-wrap text-slate-800 ${EVIDENCE_PRIMARY_READING_CLASS}`}
              quoteClassName="italic text-slate-600"
              boldClassName="font-semibold text-slate-900"
            />
          </div>

          <div className="border-t border-slate-300/70 bg-[linear-gradient(180deg,#eef2f7,#dde6f0)] px-3 py-2.5 sm:px-6 sm:py-3">
            <div className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.18em] text-slate-400 sm:flex-row sm:items-center sm:justify-between">
              <p>{email.heading}</p>
              <p className="text-slate-400/80">{resolvedPanels.recoveredFromArchiveExport}</p>
            </div>
          </div>
        </article>
      );
    })}
  </div>
  );
};

const renderNewspaperEvidence = (sections: any[], options: { panels?: EvidencePanelCopy } = {}) => {
  const resolvedPanels = {
    archivedPress: "Archived Press",
    newspaperCoverage: "Newspaper Coverage",
    pressClip: "Press Clip",
    byline: "Byline",
    date: "Date",
    staffReporter: "Staff Reporter",
    undatedClipping: "Undated clipping",
    archiveReproduction: "Archive reproduction",
    ...options.panels,
  };

  return (
  <div className="rounded-[18px] border border-amber-300/20 bg-[#171008] p-2 sm:rounded-3xl sm:p-5">
    <div className="border-b border-amber-100/15 pb-2 text-center sm:pb-3">
      <p className="text-[9px] uppercase tracking-[0.32em] text-amber-200/60 sm:text-[10px] sm:tracking-[0.35em]">
        {resolvedPanels.archivedPress}
      </p>
      <p className="mt-1 text-[1.05rem] font-semibold text-amber-50 sm:mt-2 sm:text-xl">{resolvedPanels.newspaperCoverage}</p>
    </div>
    <div className="mt-2.5 space-y-2.5 sm:mt-4 sm:space-y-4">
      {groupNewspaperArticles(sections, options.panels).map((article, idx) => {
        return (
          <article
            key={article.key || `newspaper-${idx}`}
            className="overflow-hidden rounded-[16px] border border-[#d2b26b]/45 bg-[linear-gradient(180deg,#f4ead1,#e3d4b4_46%,#dccaa7)] text-[#21170d] shadow-[0_18px_40px_rgba(0,0,0,0.24)] sm:rounded-[24px]"
          >
            <div className="border-b border-[#b9954c]/45 bg-[linear-gradient(180deg,#efe3c5,#e6d7b7)] px-2.5 py-2.5 sm:px-6 sm:py-5">
              <div className="flex flex-col gap-1.5 sm:gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[9px] uppercase tracking-[0.3em] text-[#7d6230] sm:text-[10px] sm:tracking-[0.34em]">
                      {article.publication}
                    </p>
                    <h3 className="mt-1 text-[1.12rem] font-semibold leading-[1.04] text-[#1d140b] sm:mt-1.5 sm:text-[1.85rem]">
                      {article.headline}
                    </h3>
                  </div>
                  <span className="shrink-0 rounded-full border border-[#b9954c]/45 bg-white/40 px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.16em] text-[#72582d] sm:px-3 sm:py-1 sm:text-[10px] sm:tracking-[0.18em]">
                    {resolvedPanels.pressClip}
                  </span>
                </div>

                <div className="grid gap-1.5 border-t border-[#b9954c]/35 pt-2 text-[11px] text-[#594527] sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end sm:gap-4 sm:pt-3">
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.22em] text-[#86683a] sm:text-[10px]">{resolvedPanels.byline}</p>
                    <p className="mt-0.5 text-[11px] font-medium leading-4 text-[#3b2c18] sm:mt-1 sm:text-[13px]">
                      {article.byline || resolvedPanels.staffReporter}
                    </p>
                  </div>
                  <div className="sm:text-right">
                    <p className="text-[9px] uppercase tracking-[0.22em] text-[#86683a] sm:text-[10px]">{resolvedPanels.date}</p>
                    <p className="mt-0.5 text-[11px] font-medium leading-4 text-[#3b2c18] sm:mt-1 sm:text-[13px]">
                      {article.date || resolvedPanels.undatedClipping}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[linear-gradient(180deg,#f7edd7,#efe2c4)] px-2.5 py-2.5 sm:px-6 sm:py-6">
              <div className="columns-1 gap-8 sm:columns-2">
                <RichEvidenceText
                  value={article.article}
                  className={`whitespace-pre-wrap text-[#2b1f12] ${EVIDENCE_PRIMARY_READING_CLASS}`}
                  quoteClassName="italic text-[#6b5531]"
                  boldClassName="font-semibold text-[#1d140b]"
                />
              </div>
            </div>

            <div className="border-t border-[#b9954c]/35 bg-[linear-gradient(180deg,#e6d7b7,#ddcaa7)] px-2.5 py-2 sm:px-6 sm:py-3">
              <div className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.18em] text-[#7a6032] sm:flex-row sm:items-center sm:justify-between">
                <p>{article.heading}</p>
                <p>{resolvedPanels.archiveReproduction}</p>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  </div>
  );
};

const renderPoliceReportEvidence = (
  sections: any[],
  options: { panels?: EvidencePanelCopy } = {}
) => {
  const rows = extractLabeledRows(sections, options.panels);
  const narrativeSections = sections.filter((section) => !isEntirelyStructuredSection(section));
  const resolvedPanels = {
    ottawaPoliceService: "Ottawa Police Service",
    majorCrimesDivision: "Major Crimes Division",
    incidentReport: "Incident Report",
    filedDocumentSummary: "Filed document summary",
    internalUse: "Internal Use",
    fileLabel: (caseValue: string) => `File ${caseValue}`,
    caseNumber: "Case Number",
    pendingAssignment: "Pending assignment",
    incidentWindow: "Incident Window",
    seeReportBody: "See report body",
    bodyDiscovery: "Body Discovery",
    ...options.panels,
  };
  const fieldAliasMap = buildPoliceReportFieldAliasMap(resolvedPanels);
  const caseNumber = findStructuredRowValue(rows, "caseNumber", fieldAliasMap);
  const incidentWindow = findStructuredRowValue(rows, "incidentWindow", fieldAliasMap);
  const bodyDiscovery = findStructuredRowValue(rows, "bodyDiscovery", fieldAliasMap);

  return (
    <div className="rounded-[20px] border border-sky-300/20 bg-[linear-gradient(180deg,#08111a,#0d1620)] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] sm:rounded-3xl sm:p-5">
      <div className="rounded-[18px] border border-sky-200/15 bg-slate-950/80 p-2.5 sm:rounded-2xl sm:p-5">
        <div className="border border-sky-100/10 bg-[linear-gradient(180deg,rgba(3,10,24,0.96),rgba(5,15,34,0.88))] px-2.5 py-2.5 sm:px-4 sm:py-4">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-[9px] uppercase tracking-[0.34em] text-sky-200/55 sm:text-[10px]">
                  {resolvedPanels.ottawaPoliceService}
                </p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-white/38">{resolvedPanels.majorCrimesDivision}</p>
                <p className="mt-2 text-[1.55rem] font-semibold leading-[1.02] text-white sm:text-[2rem]">
                  {resolvedPanels.incidentReport}
                </p>
                <p className="mt-1 text-[12px] leading-5 text-sky-100/58 sm:text-sm">{resolvedPanels.filedDocumentSummary}</p>
              </div>
              <div className="flex w-full flex-wrap items-start gap-2 sm:w-auto sm:flex-col sm:items-end">
                <span className="rounded-full border border-sky-300/20 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-sky-100/70 sm:px-3">
                  {resolvedPanels.internalUse}
                </span>
                {caseNumber ? (
                  <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] text-white/62 sm:px-3">
                    {resolvedPanels.fileLabel(caseNumber)}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="grid gap-px overflow-hidden rounded-[14px] border border-white/10 bg-white/10 sm:grid-cols-3 sm:rounded-[16px]">
              {[
                [resolvedPanels.caseNumber, caseNumber || resolvedPanels.pendingAssignment],
                [resolvedPanels.incidentWindow, incidentWindow || resolvedPanels.seeReportBody],
                [resolvedPanels.bodyDiscovery, bodyDiscovery || resolvedPanels.seeReportBody],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.015))] px-2.5 py-2.5 sm:px-3 sm:py-3"
                >
                  <p className="text-[9px] uppercase tracking-[0.24em] text-sky-100/48 sm:text-[10px]">
                    {label}
                  </p>
                  <RichEvidenceText
                    value={value}
                    className={`mt-1.5 text-white/84 ${EVIDENCE_SUPPORTING_READING_CLASS}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {rows.length > 0 && (
          <div className="mt-2.5 overflow-hidden rounded-[16px] border border-white/10 sm:mt-4 sm:rounded-2xl">
            {rows.map((row, idx) => (
              <div
                key={`${row.label}-${idx}`}
                className={`bg-white/[0.045] ${idx > 0 ? "border-t border-white/10" : ""} sm:grid sm:grid-cols-[148px_minmax(0,1fr)]`}
              >
                <div className="px-2.5 pb-1.5 pt-2.5 text-[9px] font-semibold uppercase tracking-[0.22em] text-sky-100 sm:border-r sm:border-white/10 sm:px-4 sm:py-3 sm:text-[11px] sm:tracking-[0.16em]">
                  {row.label}
                </div>
                <div
                  className={`border-t border-white/10 px-2.5 pb-2.5 pt-0 text-white/85 sm:border-t-0 sm:px-4 sm:py-3 ${EVIDENCE_SUPPORTING_READING_CLASS}`}
                >
                  {buildEvidenceRichTextNodes(row.value)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3 space-y-2.5 sm:mt-5 sm:space-y-4">
          {narrativeSections.map((section, idx) => (
            <section
              key={`report-${idx}`}
              className="rounded-[16px] border border-white/10 bg-black/18 p-2.5 sm:rounded-2xl sm:p-4"
            >
              <p className="text-[9px] uppercase tracking-[0.22em] text-sky-200/80 sm:text-[11px] sm:tracking-[0.18em]">
                {localizeVisibleEvidenceLabel(getSectionHeading(section, idx), options.panels)}
              </p>
              <RichEvidenceText
                value={getSectionBody(section)}
                className={`mt-2 whitespace-pre-wrap text-white/85 sm:mt-3 ${EVIDENCE_PRIMARY_READING_CLASS}`}
              />
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

const renderAutopsyEvidence = (
  sections: any[],
  options: { panels?: EvidencePanelCopy } = {}
) => {
  const rows = extractLabeledRows(sections, options.panels);
  const narrativeSections = sections.filter((section) => !isEntirelyStructuredSection(section));
  const resolvedPanels = {
    officeChiefForensicPathologist: "Office of the Chief Forensic Pathologist",
    forensicPathologyUnit: "Forensic Pathology Unit",
    postMortemExaminationReport: "Post-Mortem Examination Report",
    autopsySummary:
      "Forensic summary of injuries, physiological findings, and examiner conclusions recorded at autopsy.",
    medicalExaminerCopy: "Medical Examiner Copy",
    caseLabel: (caseValue: string) => `Case ${caseValue}`,
    decedent: "Decedent",
    age: "Age",
    sex: "Sex",
    caseFile: "Case File",
    seeReportBody: "See report body",
    notListed: "Not listed",
    pending: "Pending",
    sectionLabel: (index: number) => `Section ${String(index + 1).padStart(2, "0")}`,
    ...options.panels,
  };
  const fieldAliasMap = buildAutopsyFieldAliasMap(resolvedPanels);
  const caseNumber = findStructuredRowValue(rows, "caseNumber", fieldAliasMap);
  const decedentName = findStructuredRowValue(rows, "name", fieldAliasMap);
  const decedentAge = findStructuredRowValue(rows, "age", fieldAliasMap);
  const decedentSex = findStructuredRowValue(rows, "sex", fieldAliasMap);
  const detailRows = rows.filter(
    (row) => !matchStructuredEvidenceAlias(String(row.label || ""), fieldAliasMap)
  );

  return (
    <div className="bg-[linear-gradient(180deg,rgba(34,10,16,0.98),rgba(17,7,10,0.98))] px-1 py-1 sm:rounded-[28px] sm:border sm:border-rose-100/10 sm:bg-[radial-gradient(circle_at_top,#311118,#17090d_55%,#0c0607)] sm:px-5 sm:py-5 sm:shadow-[0_24px_90px_rgba(0,0,0,0.35)]">
      <div className="px-0 py-0 sm:rounded-2xl sm:bg-black/20">
          <div className="border-b border-rose-100/10 pb-2.5 sm:pb-4">
            <div className="flex flex-col gap-2.5 sm:gap-4">
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-[9px] uppercase tracking-[0.34em] text-rose-100/55 sm:text-[10px]">
                    {resolvedPanels.officeChiefForensicPathologist}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-rose-100/38">
                    {resolvedPanels.forensicPathologyUnit}
                  </p>
                  <h3 className="mt-1.5 font-serif text-[1.35rem] leading-[1.03] text-white sm:mt-3 sm:text-[2rem]">
                    {resolvedPanels.postMortemExaminationReport}
                  </h3>
                  <p className="mt-1 max-w-2xl text-[12px] leading-5 text-rose-50/65 sm:mt-2 sm:text-sm sm:leading-6">
                    {resolvedPanels.autopsySummary}
                  </p>
                </div>
                <div className="flex w-full flex-wrap items-start gap-2 sm:w-auto sm:flex-col sm:items-end">
                  <div className="rounded-full border border-rose-200/15 bg-rose-950/40 px-2 py-0.5 text-[9px] uppercase tracking-[0.16em] text-rose-100/75 sm:px-3 sm:py-1 sm:text-[10px] sm:tracking-[0.18em]">
                    {resolvedPanels.medicalExaminerCopy}
                  </div>
                  {caseNumber ? (
                    <div className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-white/62 sm:px-3 sm:py-1 sm:text-[10px] sm:tracking-[0.16em]">
                      {resolvedPanels.caseLabel(caseNumber)}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-px overflow-hidden border-y border-rose-100/10 bg-rose-100/10 sm:rounded-[16px] sm:border sm:grid-cols-4">
                {[
                  [resolvedPanels.decedent, decedentName || resolvedPanels.seeReportBody],
                  [resolvedPanels.age, decedentAge || resolvedPanels.notListed],
                  [resolvedPanels.sex, decedentSex || resolvedPanels.notListed],
                  [resolvedPanels.caseFile, caseNumber || resolvedPanels.pending],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.015))] px-2 py-2 sm:px-3 sm:py-3"
                  >
                    <p className="text-[9px] uppercase tracking-[0.24em] text-rose-100/50 sm:text-[10px]">{label}</p>
                    <RichEvidenceText
                      value={value}
                      className={`mt-0.5 text-rose-50/86 sm:mt-1 ${EVIDENCE_SUPPORTING_READING_CLASS}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {detailRows.length > 0 ? (
            <div className="mt-2.5 overflow-hidden border-y border-rose-100/10 bg-black/10 sm:mt-5 sm:rounded-2xl sm:border">
              <div className="grid gap-px bg-rose-100/10 md:grid-cols-2">
                {detailRows.map((row, idx) => (
                  <div
                    key={`${row.label}-${idx}`}
                    className="grid min-h-[64px] grid-rows-[auto_1fr] bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))] px-2 py-2 sm:min-h-[84px] sm:px-4 sm:py-3"
                  >
                    <p className="text-[9px] font-semibold uppercase tracking-[0.22em] text-rose-100/60 sm:text-[10px]">
                      {row.label}
                    </p>
                    <RichEvidenceText
                      value={row.value}
                      className={`mt-1.5 text-rose-50/88 sm:mt-3 ${EVIDENCE_SUPPORTING_READING_CLASS}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="mt-2.5 divide-y divide-rose-100/10 sm:mt-6 sm:space-y-0">
            {narrativeSections.map((section, idx) => (
              <section
                key={`autopsy-${idx}`}
                className="bg-transparent py-2 first:pt-0 last:pb-0 sm:rounded-2xl sm:border sm:border-rose-100/10 sm:bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.01))] sm:p-5 sm:not-first:mt-4"
              >
                <div className="flex items-center gap-2 border-b border-rose-100/10 pb-2 sm:gap-3 sm:pb-3">
                  <span className="rounded-full border border-rose-200/15 bg-rose-950/40 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.18em] text-rose-100/55 sm:px-2.5 sm:py-1 sm:text-[10px] sm:tracking-[0.22em]">
                    {resolvedPanels.sectionLabel(idx)}
                  </span>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-rose-100/78 sm:text-xs sm:tracking-[0.18em]">
                    {localizeVisibleEvidenceLabel(getSectionHeading(section, idx), options.panels)}
                  </p>
                </div>
                <RichEvidenceText
                  value={getSectionBody(section)}
                  className={`mt-2 whitespace-pre-wrap text-rose-50/86 sm:mt-4 ${EVIDENCE_PRIMARY_READING_CLASS}`}
                />
              </section>
            ))}
          </div>
      </div>
    </div>
  );
};

const renderCrimeScenePhotosEvidence = (
  sections: any[],
  images: Array<{ storageKey: string; url: string; description?: string }>,
  imagePrompt: string,
  options: {
    finalView?: boolean;
    onOpenImage?: (url: string, alt: string) => void;
    copy?: EvidenceMediaCopy;
    panels?: EvidencePanelCopy;
  } = {}
) => {
  const resolvedPanels = {
    photoPacket: "Photo Packet",
    crimeScenePhotos: "Crime Scene Photos",
    imageCount: (count: number) => `${count} image${count === 1 ? "" : "s"}`,
    noCrimeScenePhotos: "No crime scene photos linked yet.",
    imagePrompt: "Image Prompt",
    ...options.panels,
  };

  return (
  <div className="space-y-4">
    <div className="rounded-[24px] border border-white/10 bg-black/20 p-3.5 sm:rounded-3xl sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-white/45 sm:tracking-[0.28em]">{resolvedPanels.photoPacket}</p>
          <p className="mt-1 text-lg font-semibold leading-tight text-white sm:text-xl">{resolvedPanels.crimeScenePhotos}</p>
        </div>
        <span className="rounded-full border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-wide text-white/55 sm:px-3">
          {resolvedPanels.imageCount(images.length)}
        </span>
      </div>
      <div className="mt-3 sm:mt-4">
        {renderImageStrip(images, resolvedPanels.noCrimeScenePhotos, {
          finalView: options.finalView,
          onOpenImage: options.onOpenImage,
          copy: options.copy,
        })}
      </div>
    </div>
    {!options.finalView && imagePrompt ? (
      <div className="rounded-[22px] border border-amber-300/20 bg-amber-500/10 p-3 sm:rounded-3xl sm:p-4">
        <p className="text-[10px] uppercase tracking-[0.22em] text-amber-200/70">{resolvedPanels.imagePrompt}</p>
        <RichEvidenceText
          value={imagePrompt}
          className="mt-2.5 whitespace-pre-wrap text-sm leading-6 text-white/80 sm:mt-3 sm:leading-7"
        />
      </div>
    ) : null}
    {!options.finalView ? renderGenericEvidence(sections, options.panels) : null}
  </div>
  );
};

const renderSuspectCards = (
  suspects: any[],
  suspectImageAccessMap: Record<string, string>,
  caseId: string,
  options: {
    finalView?: boolean;
    onOpenImage?: (url: string, alt: string) => void;
    witnessSections?: any[];
    portraitAssignmentAudit?: any[];
    copy?: EvidenceMediaCopy;
    panels?: EvidencePanelCopy;
  } = {}
) => (
  <div className="space-y-3">
    {suspects.length > 0 ? (
      suspects.map((suspect: any, idx: number) => {
        const storageKey = getSuspectImageRef(suspect);
        const imageUrl = String(suspect?.image_url || "").trim() || suspectImageAccessMap[storageKey] || "";
        const portraitPrompt = String(suspect?.image_prompt || "").trim() || buildSuspectImagePrompt(suspect || {});
        const resolvedPanels = {
          suspectDossiers: "Suspect Dossiers",
          suspectCount: (count: number) => `${count} suspect${count === 1 ? "" : "s"}`,
          openDossier: "Open Dossier",
          closeDossier: "Close Dossier",
          portraitPendingSuspect: "Portrait pending. Upload a suspect image to complete this dossier.",
          suspectEvidence: "Suspect Evidence",
          noSuspectEvidence:
            "No suspect evidence has been authored yet. Add alibi, evidence pressure, and opportunity details in the suspect editor.",
          witnessMentions: "Witness Mentions",
          witnessMentionCount: (count: number) => `${count} witness mention${count === 1 ? "" : "s"}`,
          noSuspects: "No suspects attached to this draft yet.",
          noRoleProvided: "No role provided",
          motive: "Motive",
          age: "Age",
          sex: "Sex",
          hair: "Hair",
          eyes: "Eyes",
          height: "Height",
          build: "Build",
          maritalStatus: "Marital Status",
          occupation: "Occupation",
          employer: "Employer",
          alibi: "Alibi",
          evidenceAgainst: "Evidence Against",
          evidenceInFavor: "Evidence In Favor",
          opportunity: "Opportunity",
          behaviorNotes: "Behavior Notes",
          portraitPrompt: "Portrait Prompt",
          ...options.panels,
        };
        const factRows = [
          [resolvedPanels.motive, suspect?.motive],
          [resolvedPanels.age, suspect?.age],
          [resolvedPanels.sex, suspect?.sex],
          [resolvedPanels.hair, suspect?.hair],
          [resolvedPanels.eyes, suspect?.eyes],
          [resolvedPanels.height, suspect?.height],
          [resolvedPanels.build, suspect?.build],
          [resolvedPanels.maritalStatus, suspect?.marital_status],
          [resolvedPanels.occupation, suspect?.occupation],
          [resolvedPanels.employer, suspect?.employer],
        ].filter(([, value]) => String(value || "").trim());
        const evidenceRows = [
          [resolvedPanels.alibi, suspect?.alibi],
          [resolvedPanels.evidenceAgainst, suspect?.evidence_against],
          [resolvedPanels.evidenceInFavor, suspect?.evidence_in_favor],
          [resolvedPanels.opportunity, suspect?.opportunity],
          [resolvedPanels.behaviorNotes, suspect?.behavior_notes],
        ].filter(([, value]) => String(value || "").trim());
        const witnessMentions = getWitnessStatementGroups(
          Array.isArray(options.witnessSections) ? options.witnessSections : [],
          [suspect]
        ).matchedGroups[0]?.statements || [];
        const portraitIndicator = resolvePortraitAssignmentIndicator({
          suspect,
          suspectIndex: idx,
          auditEntries: Array.isArray(options.portraitAssignmentAudit) ? options.portraitAssignmentAudit : [],
        });
        return (
          <details
            key={`${suspect?.name || "suspect"}-${idx}`}
            className="group overflow-hidden rounded-3xl border border-white/10 bg-black/20 open:border-cyan-300/20 open:bg-[linear-gradient(180deg,rgba(34,211,238,0.08),rgba(255,255,255,0.02))]"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-4 marker:content-none">
              <div className="min-w-0">
                <p className="text-base font-semibold text-white">{suspect?.name || `Suspect ${idx + 1}`}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/45">
                  {suspect?.role || suspect?.relation || resolvedPanels.noRoleProvided}
                </p>
                {portraitIndicator ? (
                  <p className="mt-2 text-xs text-white/60">{portraitIndicator.detail}</p>
                ) : null}
              </div>
              <div className="hidden flex-wrap justify-end gap-2 sm:flex">
                {portraitIndicator ? (
                  <span className={`rounded-full border px-2.5 py-1 text-[11px] ${portraitIndicator.badgeClassName}`}>
                    {portraitIndicator.badgeLabel}
                  </span>
                ) : null}
                {witnessMentions.length > 0 ? (
                  <span className="rounded-full border border-cyan-300/20 bg-cyan-400/10 px-2.5 py-1 text-[11px] text-cyan-50/85">
                    {resolvedPanels.witnessMentionCount(witnessMentions.length)}
                  </span>
                ) : null}
                {String(suspect?.motive || "").trim() ? (
                  <span className="rounded-full border border-amber-300/20 bg-amber-500/10 px-2.5 py-1 text-[11px] text-amber-50/85">
                    {String(suspect.motive)}
                  </span>
                ) : null}
                {String(suspect?.occupation || "").trim() ? (
                  <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] text-white/75">
                    {String(suspect.occupation)}
                  </span>
                ) : null}
              </div>
              <span className="rounded-full border border-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/65 transition group-open:border-cyan-300/25 group-open:text-cyan-100">
                <span className="group-open:hidden">{resolvedPanels.openDossier}</span>
                <span className="hidden group-open:inline">{resolvedPanels.closeDossier}</span>
              </span>
            </summary>

            <div className="border-t border-white/10 px-4 pb-4 pt-4">
              <div className="grid items-stretch gap-4 lg:grid-cols-[0.82fr_1.18fr]">
                <div className="flex h-full flex-col gap-3">
                  {imageUrl ? (
                    <div className="relative flex-1">
                      <img
                        src={imageUrl}
                        alt={suspect?.name || `Suspect ${idx + 1}`}
                        className="aspect-[4/5] h-full w-full cursor-zoom-in rounded-2xl border border-white/10 object-cover"
                        onDoubleClick={() =>
                          options.onOpenImage?.(imageUrl, suspect?.name || `Suspect ${idx + 1}`)
                        }
                      />
                      <button
                        type="button"
                        onClick={() =>
                          options.onOpenImage?.(imageUrl, suspect?.name || `Suspect ${idx + 1}`)
                        }
                        className="absolute bottom-3 right-3 rounded-full border border-white/15 bg-black/60 px-3 py-1.5 text-[11px] font-semibold text-white/90 hover:bg-black/75"
                      >
                        {options.copy?.openLarge || "Open Large"}
                      </button>
                    </div>
                  ) : (
                    <div className="flex aspect-[4/5] h-full w-full flex-1 items-center justify-center rounded-2xl border border-dashed border-white/15 bg-white/5 p-6 text-center text-sm text-white/55">
                      {resolvedPanels.portraitPendingSuspect}
                    </div>
                  )}
                  {options.finalView ? null : (
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={`/club/admin/cases/${caseId}/suspect/${idx}/edit`}
                        className="inline-flex rounded-full border border-cyan-300/25 bg-cyan-400/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-100 hover:bg-cyan-400/15"
                      >
                        Edit Suspect
                      </Link>
                    </div>
                  )}
                </div>

                <div className="flex h-full flex-col gap-3">
                  <div className="grid flex-1 gap-0 overflow-hidden rounded-xl border border-white/10">
                    {factRows.map(([label, value], rowIdx) => (
                      <div
                        key={`${suspect?.name || idx}-${label}`}
                        className={`grid grid-cols-1 bg-white/5 sm:grid-cols-[120px_minmax(0,1fr)] ${rowIdx > 0 ? "border-t border-white/10" : ""}`}
                      >
                        <div className="border-b border-white/10 px-3 py-2 text-xs font-semibold text-cyan-100 sm:border-b-0 sm:border-r">
                          {label}
                        </div>
                        <div className="px-3 py-2 text-xs text-white/80">{String(value)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-2xl border border-cyan-300/20 bg-cyan-500/10 p-4">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-cyan-200/70">{resolvedPanels.suspectEvidence}</p>
                    {evidenceRows.length > 0 ? (
                      <div className="mt-3 grid gap-0 overflow-hidden rounded-xl border border-white/10">
                        {evidenceRows.map(([label, value], rowIdx) => (
                          <div
                            key={`${suspect?.name || idx}-evidence-${label}`}
                            className={`grid grid-cols-1 bg-white/5 sm:grid-cols-[132px_minmax(0,1fr)] ${rowIdx > 0 ? "border-t border-white/10" : ""}`}
                          >
                            <div className="border-b border-white/10 px-3 py-2 text-xs font-semibold text-cyan-100 sm:border-b-0 sm:border-r">
                              {String(label)}
                            </div>
                            <RichEvidenceText
                              value={String(value)}
                              className={`whitespace-pre-wrap px-3 py-2 text-white/80 ${EVIDENCE_SUPPORTING_READING_CLASS}`}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={`mt-3 text-white/60 ${EVIDENCE_SUPPORTING_READING_CLASS}`}>
                        {resolvedPanels.noSuspectEvidence}
                      </p>
                    )}
                  </div>
                  {witnessMentions.length > 0 ? (
                    <div className="rounded-2xl border border-amber-300/20 bg-amber-500/10 p-4">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-amber-200/70">{resolvedPanels.witnessMentions}</p>
                      <div className="mt-3 space-y-3">
                        {witnessMentions.map(({ section, index }: any) => (
                          <div key={`suspect-witness-${idx}-${index}`} className="rounded-xl border border-white/10 bg-black/20 p-3">
                            <p className="text-[11px] uppercase tracking-[0.16em] text-white/45">
                              {getSectionHeading(section, index)}
                            </p>
                            <RichEvidenceText
                              value={getSectionBody(section)}
                              className={`mt-2 text-white/80 ${EVIDENCE_SUPPORTING_READING_CLASS}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {!options.finalView ? (
                    <div className="rounded-2xl border border-amber-300/20 bg-amber-500/10 p-4">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-amber-200/70">{resolvedPanels.portraitPrompt}</p>
                      <p className="mt-3 whitespace-pre-wrap text-xs leading-6 text-white/70">{portraitPrompt}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </details>
        );
      })
    ) : (
      <div className="rounded-3xl border border-dashed border-white/15 bg-white/5 p-6 text-sm text-white/55">
        {options.panels?.noSuspects || "No suspects attached to this draft yet."}
      </div>
    )}
  </div>
);

const renderVictimProfileEvidence = (
  sections: any[],
  images: Array<{ storageKey: string; url: string; description?: string }>,
  imagePrompt: string,
  suspects: any[],
  suspectImageAccessMap: Record<string, string>,
  caseId: string,
  options: {
    finalView?: boolean;
    onOpenImage?: (url: string, alt: string) => void;
    witnessSections?: any[];
    portraitAssignmentAudit?: any[];
    copy?: EvidenceMediaCopy;
    panels?: EvidencePanelCopy;
  } = {}
) => {
  const victimFieldAliasMap = buildVictimProfileFieldAliasMap(options.panels);
  const rowsFromLines = extractProfileRowsFromSections(sections).map((row) => ({
    ...row,
    canonicalKey: matchStructuredEvidenceAlias(String(row.label || ""), victimFieldAliasMap),
  }));
  const rowsFromSections = (Array.isArray(sections) ? sections : [])
    .map((section, idx) => {
      const heading = String(getSectionHeading(section, idx) || "").trim();
      const body = String(getSectionBody(section) || "").trim();
      if (!heading || !body) return null;
      if (body.includes("\n")) return null;
      const canonicalKey = matchStructuredEvidenceAlias(heading, victimFieldAliasMap);
      if (!canonicalKey) return null;
      return { label: heading, value: body, canonicalKey };
    })
    .filter(Boolean) as Array<{ label: string; value: string; canonicalKey: string }>;
  const combinedRows = [...rowsFromLines, ...rowsFromSections];
  const rows = combinedRows.filter(
    (row, idx) =>
      combinedRows.findIndex(
        (candidate) =>
          String(candidate.label || "").trim().toLowerCase() ===
            String(row.label || "").trim().toLowerCase() &&
          String(candidate.value || "").trim().toLowerCase() ===
            String(row.value || "").trim().toLowerCase()
      ) === idx
  );
  const narrativeSections = sections.filter((section, idx) => {
    const heading = String(getSectionHeading(section, idx) || "").trim().toLowerCase();
    const body = getSectionBody(section);
    if (/^([^:]+):\s*(.+)$/.test(body)) return false;
    if (matchStructuredEvidenceAlias(heading, victimFieldAliasMap) && !body.includes("\n")) return false;
    return true;
  });
  const resolvedImagePrompt =
    String(imagePrompt || "").trim() || buildVictimProfileImagePrompt({ sections });
  const image = images[0] || null;
  const imageUrl = String(image?.url || "").trim();
  const narrativeHeading =
    narrativeSections.length > 0
      ? String(getSectionHeading(narrativeSections[0], 0) || "Victim Narrative").trim()
      : "Victim Narrative";
  const resolvedPanels = {
    victimProfile: "Victim Profile",
    identitySummary: "Identity Summary",
    dossier: "dossier",
    victimPortrait: "Victim portrait",
    portraitPendingVictim: "Portrait pending. Upload a victim image to complete this dossier.",
    noStructuredProfileFields: "No structured profile fields generated yet.",
    noVictimNarrative:
      "No victim narrative text was generated yet. The profile currently contains structured identity fields only.",
    victimProfiles: "Victim Profiles",
    suspectDossiers: "Suspect Dossiers",
    suspectCount: (count: number) => `${count} suspect${count === 1 ? "" : "s"}`,
    imagePrompt: "Image Prompt",
    ...options.panels,
  };

  return (
    <div className="space-y-3 sm:space-y-5">
      <div className="rounded-[22px] border border-sky-300/20 bg-[linear-gradient(180deg,#03101f,#0d1420)] p-2.5 sm:rounded-3xl sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-sky-200/60 sm:tracking-[0.28em]">
              {resolvedPanels.victimProfile}
            </p>
            <p className="mt-1 text-[1.05rem] font-bold leading-tight text-white sm:mt-2 sm:text-2xl">{resolvedPanels.identitySummary}</p>
          </div>
          <span className="rounded-full border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] text-white/55">
            {resolvedPanels.dossier}
          </span>
        </div>

        <div className="mt-2.5 grid gap-2.5 lg:mt-3 lg:grid-cols-[0.82fr_1.18fr] lg:gap-3">
          <div className="flex h-full flex-col gap-2.5 sm:gap-3">
            {imageUrl ? (
              <div className="relative flex-1">
                <img
                  src={imageUrl}
                  alt={rows.find((row) => row.canonicalKey === "name")?.value || resolvedPanels.victimPortrait}
                  className="aspect-[4/5] h-full w-full cursor-zoom-in rounded-[18px] border border-white/10 object-cover sm:rounded-2xl"
                  onDoubleClick={() =>
                    options.onOpenImage?.(
                      imageUrl,
                      rows.find((row) => row.canonicalKey === "name")?.value || resolvedPanels.victimPortrait
                    )
                  }
                />
                <button
                  type="button"
                  onClick={() =>
                    options.onOpenImage?.(
                      imageUrl,
                      rows.find((row) => row.canonicalKey === "name")?.value || resolvedPanels.victimPortrait
                    )
                  }
                  className="absolute bottom-2.5 right-2.5 rounded-full border border-white/15 bg-black/60 px-2.5 py-1.5 text-[10px] font-semibold text-white/90 hover:bg-black/75 sm:bottom-3 sm:right-3 sm:px-3 sm:text-[11px]"
                >
                  {options.copy?.openLarge || "Open Large"}
                </button>
              </div>
            ) : (
              <div className="flex aspect-[4/5] w-full flex-1 items-center justify-center rounded-[18px] border border-dashed border-white/15 bg-white/5 p-4 text-center text-sm text-white/55 sm:rounded-2xl sm:p-6">
                {resolvedPanels.portraitPendingVictim}
              </div>
            )}
          </div>

          <div className="flex h-full flex-col gap-2.5 sm:gap-3">
            <div className="grid flex-1 gap-0 overflow-hidden rounded-[18px] border border-white/10 sm:rounded-xl">
              {rows.length > 0 ? (
                rows.map((row, idx) => (
                  <div
                    key={`${row.label}-${idx}`}
                    className={`grid grid-cols-[98px_minmax(0,1fr)] bg-white/5 sm:grid-cols-[128px_minmax(0,1fr)] ${idx > 0 ? "border-t border-white/10" : ""}`}
                  >
                    <div className="border-r border-white/10 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-100 sm:px-3 sm:py-2 sm:text-xs sm:normal-case sm:tracking-normal">
                      {row.label}
                    </div>
                    <div className="px-2.5 py-1.5 text-[10px] leading-4.5 text-white/82 sm:px-3 sm:py-2 sm:text-xs sm:leading-5">
                      {buildEvidenceRichTextNodes(row.value)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-2.5 py-2.5 text-sm text-white/60 sm:px-3 sm:py-3">{resolvedPanels.noStructuredProfileFields}</div>
              )}
            </div>
            {!options.finalView && resolvedImagePrompt ? (
              <div className="rounded-[18px] border border-amber-300/20 bg-amber-500/10 p-2.5 sm:rounded-2xl sm:p-4">
                <p className="text-[10px] uppercase tracking-[0.18em] text-amber-200/70">{resolvedPanels.imagePrompt}</p>
                <RichEvidenceText
                  value={resolvedImagePrompt}
                  className="mt-2 whitespace-pre-wrap text-[11px] leading-5 text-white/75 sm:mt-3 sm:text-sm sm:leading-7"
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="rounded-[22px] border border-white/10 bg-zinc-950/60 p-2.5 sm:rounded-3xl sm:p-5">
        {narrativeSections.length > 0 ? (
          <div className="mb-3.5 space-y-2 sm:mb-5 sm:space-y-3">
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">{narrativeHeading}</p>
            {renderGenericEvidence(narrativeSections, options.panels)}
          </div>
        ) : (
          <div className="mb-3.5 rounded-[18px] border border-dashed border-white/15 bg-white/5 p-2.5 text-sm text-white/55 sm:mb-5 sm:rounded-2xl sm:p-4">
            {resolvedPanels.noVictimNarrative}
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">{resolvedPanels.victimProfiles}</p>
            <p className="mt-1 text-base font-semibold text-white sm:text-xl">{resolvedPanels.suspectDossiers}</p>
          </div>
          <span className="rounded-full border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] text-white/55 sm:px-3">
            {resolvedPanels.suspectCount(suspects.length)}
          </span>
        </div>
        <div className="mt-3 sm:mt-4">
          {renderSuspectCards(suspects, suspectImageAccessMap, caseId, {
            finalView: options.finalView,
            onOpenImage: options.onOpenImage,
            witnessSections: Array.isArray(options.witnessSections) ? options.witnessSections : [],
            portraitAssignmentAudit: Array.isArray(options.portraitAssignmentAudit)
              ? options.portraitAssignmentAudit
              : [],
            copy: options.copy,
            panels: options.panels,
          })}
        </div>
      </div>
    </div>
  );
};

const SolutionEvidence = ({
  sections,
  caseTitle,
  solveGate,
}: {
  sections: any[];
  caseTitle?: string;
  solveGate?: {
    enabled?: boolean;
    mode?: "member" | "guest" | "custom";
    suspects?: any[];
    suspectImageAccessMap?: Record<string, string>;
    actorLabel?: string;
    currentRankLabel?: string;
    solveStatus?: {
      attemptCount?: number;
      hasSubmittedWarrant?: boolean;
      selectedSuspectIndex?: number | null;
      selectedSuspectName?: string;
      isCorrect?: boolean;
      solutionUnlocked?: boolean;
      successfulSolvesCount?: number;
      nextRankLabel?: string;
      solvesToNextRank?: number;
    } | null;
    onSubmitWarrant?: (suspectIndex: number) => Promise<void>;
    onDevResetWarrant?: () => Promise<void>;
    busy?: boolean;
    feedback?: string;
    error?: string;
    copy?: SolutionEvidenceCopy;
  };
}) => {
  const resolvedCopy = {
    finalRevealEyebrow: "Final Reveal",
    readyTitle: "Read This Only When You're Ready To Solve The Case",
    readyDescription:
      "This file contains the full answer, the culprit, and the reasoning chain. Open it only after you have made your final accusation.",
    warningEyebrow: "Warning",
    warningDescription:
      "Once you open the solution, the mystery is gone. Pause here if your group still wants to debate suspects, motive, means, and opportunity.",
    arrestWarrant: "Arrest Warrant",
    greetingCustom: (actorLabel: string) => `Congratulations on reaching the end of the case, ${actorLabel}.`,
    greetingMember: (rankLabel: string, actorLabel: string) =>
      `Congratulations on reaching the end of the case, ${rankLabel} ${actorLabel}.`,
    arrestPrompt:
      "Now is the time to request an arrest warrant. Select the suspect you believe masterminded this crime.",
    suspectSelection: "Suspect Selection",
    selectedSuspect: "Selected suspect",
    noPhoto: "No Photo",
    suspectDossier: "Suspect dossier",
    submitting: "Submitting...",
    warrantApproved: "Warrant Approved",
    warrantRequest: "Submit Warrant Request",
    warrantFiled: "Warrant Filed",
    previewOnly: "Preview Only",
    previewOnlyDescription:
      "Preview mode only. Suspect selection is shown here for content verification.",
    attempts: "Attempts",
    filedSuspect: "Filed suspect",
    unknownSuspect: "Unknown suspect",
    result: "Result",
    correctWarrantApproved: "Correct warrant approved",
    incorrectSuspectTryAgain: "Incorrect suspect. Try again",
    correctFirstWarrant: "Correct first warrant",
    solutionUnlockedWithoutSolveCredit: "Solution unlocked without solve credit",
    recordSummary: (count: number, nextRankLabel?: string, solvesToNextRank?: number) =>
      `Record: ${count} successful solve${count === 1 ? "" : "s"}${
        nextRankLabel ? `, ${Number(solvesToNextRank || 0)} until ${nextRankLabel}.` : "."
      }`,
    customSolutionUnlocked: "The official solution is now unlocked for this custom case.",
    customRetryPrompt:
      "That warrant was not correct. Review the evidence and file another warrant request.",
    guestSessionUnlocked: "This guest-session result unlocks the solution for this session only.",
    solutionAccess: "Solution Access",
    openOfficialSolution: "Open The Official Case Solution",
    reveal: "Reveal",
    hide: "Hide",
    submitCorrectPrompt: "Submit the correct warrant above to unlock the official solution.",
    submitWarrantPrompt: "Submit a warrant request above to unlock the official solution.",
    shareTheWin: "Share The Win",
    shareDescription: "Post your result with one tap or copy the message below for social media.",
    shareNow: "Share Now",
    copied: "Copied",
    copyFailed: "Copy Failed",
    copyShareText: "Copy Share Text",
    shareTextCustom: (caseTitle: string) =>
      `ColdCaseGameKits.com - Just solved our custom cold case "${caseTitle}"`,
    shareTextAgency: (caseTitle: string) =>
      `We solved "${caseTitle}" at the Cold Case Agency. ${COLD_CASE_WEBSITE_URL}`,
    nativeShareTitle: (caseTitle: string) => `We solved "${caseTitle}"`,
    ...solveGate?.copy,
  };
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");
  const [selectedSuspectIndex, setSelectedSuspectIndex] = useState<number>(() =>
    Number.isInteger(Number(solveGate?.solveStatus?.selectedSuspectIndex))
      ? Number(solveGate?.solveStatus?.selectedSuspectIndex)
      : 0
  );
  const resolvedCaseTitle = String(caseTitle || "").trim() || "this case";
  const isCustomSolve = solveGate?.mode === "custom";
  const shareText = isCustomSolve
    ? resolvedCopy.shareTextCustom(resolvedCaseTitle)
    : resolvedCopy.shareTextAgency(resolvedCaseTitle);
  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";
  const suspects = Array.isArray(solveGate?.suspects) ? solveGate?.suspects : [];
  const suspectImageAccessMap = solveGate?.suspectImageAccessMap || {};
  const solveStatus = solveGate?.solveStatus || null;
  const hasWarrantAction = typeof solveGate?.onSubmitWarrant === "function";
  const hasDevResetAction = typeof solveGate?.onDevResetWarrant === "function";
  const allowHiddenCustomReset = Boolean(import.meta.env.DEV && isCustomSolve && hasDevResetAction);
  const canAccessSolution = solveGate?.enabled === false || Boolean(solveStatus?.solutionUnlocked);
  const greetingRank =
    String(solveGate?.currentRankLabel || "").trim() ||
    "Detective";
  const greetingActor = String(solveGate?.actorLabel || "Detective").trim() || "Detective";
  const greetingLine = isCustomSolve
    ? resolvedCopy.greetingCustom(greetingActor)
    : resolvedCopy.greetingMember(greetingRank, greetingActor);
  const shouldDisableWarrantInputs =
    Boolean(solveGate?.busy) ||
    (!isCustomSolve && Boolean(solveStatus?.hasSubmittedWarrant)) ||
    (isCustomSolve && Boolean(solveStatus?.solutionUnlocked));
  const customWarrantRetryable = isCustomSolve && Boolean(solveStatus?.hasSubmittedWarrant) && !canAccessSolution;
  const feedbackTone =
    customWarrantRetryable ? "warning" : "success";
  const selectedSuspect = suspects[selectedSuspectIndex] || null;
  const [devResetClickCount, setDevResetClickCount] = useState(0);

  useEffect(() => {
    if (Number.isInteger(Number(solveGate?.solveStatus?.selectedSuspectIndex))) {
      setSelectedSuspectIndex(Number(solveGate?.solveStatus?.selectedSuspectIndex));
    }
  }, [solveGate?.solveStatus?.selectedSuspectIndex]);

  useEffect(() => {
    if (!suspects.length) return;
    if (
      Number.isInteger(selectedSuspectIndex) &&
      selectedSuspectIndex >= 0 &&
      selectedSuspectIndex < suspects.length
    ) {
      return;
    }
    devLog("case", "solution_suspect_selection_reset", {
      caseTitle: resolvedCaseTitle,
      mode: String(solveGate?.mode || "unknown"),
      previousSelectedSuspectIndex: selectedSuspectIndex,
      suspectCount: suspects.length,
    });
    setSelectedSuspectIndex(0);
  }, [resolvedCaseTitle, selectedSuspectIndex, solveGate?.mode, suspects.length]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopyStatus("copied");
      window.setTimeout(() => setCopyStatus("idle"), 2000);
    } catch {
      setCopyStatus("error");
      window.setTimeout(() => setCopyStatus("idle"), 2500);
    }
  };

  const handleNativeShare = async () => {
    if (!canNativeShare) return;
    try {
      await navigator.share({
        title: resolvedCopy.nativeShareTitle(resolvedCaseTitle),
        text: shareText,
        url: COLD_CASE_WEBSITE_URL,
      });
    } catch {
      // Ignore cancellation from native share sheets.
    }
  };

  const handleSubmitWarrant = async () => {
    if (allowHiddenCustomReset && solveStatus?.solutionUnlocked) {
      const nextCount = devResetClickCount + 1;
      if (nextCount >= 3) {
        setDevResetClickCount(0);
        await solveGate?.onDevResetWarrant?.();
        return;
      }
      setDevResetClickCount(nextCount);
      window.setTimeout(() => {
        setDevResetClickCount((current) => (current === nextCount ? 0 : current));
      }, 1500);
      return;
    }
    setDevResetClickCount(0);
    if (!solveGate?.onSubmitWarrant) return;
    await solveGate.onSubmitWarrant(selectedSuspectIndex);
  };

  return (
    <div className="overflow-hidden bg-[linear-gradient(180deg,rgba(18,10,12,0.98),rgba(10,9,11,0.98))] sm:rounded-[24px] sm:border sm:border-red-200/10 sm:bg-[radial-gradient(circle_at_top,rgba(127,29,29,0.55),rgba(16,8,10,0.98)_55%),linear-gradient(180deg,#14090b,#09090b)]">
        <div className="border-b border-red-200/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.01))] px-2.5 py-2.5 sm:px-5 sm:py-4">
          <p className="text-[10px] uppercase tracking-[0.26em] text-red-200/60">{resolvedCopy.finalRevealEyebrow}</p>
          <p className="mt-1 text-[1.15rem] font-semibold leading-tight text-white sm:text-2xl">{resolvedCopy.readyTitle}</p>
          <p className={`mt-1.5 max-w-2xl text-red-50/70 ${EVIDENCE_SUPPORTING_READING_CLASS}`}>
            {resolvedCopy.readyDescription}
          </p>
        </div>

        <div className="px-2.5 py-2.5 sm:px-5 sm:py-5">
          <div className="rounded-[14px] border border-amber-300/20 bg-[linear-gradient(180deg,rgba(120,53,15,0.22),rgba(255,255,255,0.02))] px-2.5 py-2.5 sm:rounded-[18px] sm:px-4 sm:py-4">
            <div className="flex items-start gap-2.5 sm:gap-3">
              <div className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full bg-amber-300 shadow-[0_0_0_4px_rgba(252,211,77,0.16)] sm:h-3 sm:w-3" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-amber-100/70">{resolvedCopy.warningEyebrow}</p>
                <p className={`mt-1 text-amber-50/88 ${EVIDENCE_SUPPORTING_READING_CLASS}`}>
                  {resolvedCopy.warningDescription}
                </p>
              </div>
            </div>
          </div>

          {solveGate?.enabled !== false ? (
            <div className="mt-2.5 rounded-[16px] border border-cyan-300/20 bg-[linear-gradient(180deg,rgba(22,78,99,0.22),rgba(255,255,255,0.03))] px-3 py-3 sm:mt-4 sm:px-4 sm:py-4">
              <p className="text-[10px] uppercase tracking-[0.22em] text-cyan-100/70">
                {resolvedCopy.arrestWarrant}
              </p>
              <p className="mt-1 text-[1rem] font-semibold text-white sm:text-lg">
                {greetingLine}
              </p>
              <p className={`mt-2 text-cyan-50/88 ${EVIDENCE_SUPPORTING_READING_CLASS}`}>
                {resolvedCopy.arrestPrompt}
              </p>

              <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-cyan-100/65">
                    {resolvedCopy.suspectSelection}
                  </span>
                  <div className="mt-2 rounded-[16px] border border-white/10 bg-black/20 p-2">
                    <select
                      value={selectedSuspectIndex}
                      onChange={(event) => setSelectedSuspectIndex(Number(event.target.value))}
                      disabled={shouldDisableWarrantInputs}
                      className="w-full rounded-[12px] border border-white/10 bg-[#08111a] px-3 py-3 text-sm text-white outline-none"
                    >
                      {suspects.map((suspect, index) => (
                        <option key={`${suspect?.name || "suspect"}-${index}`} value={index}>
                          {String(suspect?.name || `Suspect ${index + 1}`)}
                        </option>
                      ))}
                    </select>
                    {selectedSuspect ? (
                      <div className="mt-3 flex items-center gap-3 rounded-[12px] border border-white/10 bg-white/5 px-3 py-2.5">
                        {(() => {
                          const imageRef = getSuspectImageRef(selectedSuspect);
                          const imageUrl =
                            String(selectedSuspect?.image_url || "").trim() ||
                            String(suspectImageAccessMap[imageRef] || "").trim();
                          return imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={String(selectedSuspect?.name || resolvedCopy.selectedSuspect)}
                              className="h-12 w-12 rounded-xl object-cover"
                            />
                          ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-dashed border-white/15 bg-white/5 text-[10px] uppercase tracking-[0.16em] text-white/45">
                              {resolvedCopy.noPhoto}
                            </div>
                          );
                        })()}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-white">
                            {String(selectedSuspect?.name || resolvedCopy.selectedSuspect)}
                          </p>
                          <p className="truncate text-[11px] uppercase tracking-[0.16em] text-cyan-100/65">
                            {String(selectedSuspect?.role || selectedSuspect?.relation || resolvedCopy.suspectDossier)}
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </label>

                <button
                  type="button"
                  disabled={
                    !hasWarrantAction ||
                    Boolean(solveGate?.busy) ||
                    (shouldDisableWarrantInputs && !(allowHiddenCustomReset && Boolean(solveStatus?.solutionUnlocked))) ||
                    suspects.length === 0
                  }
                  onClick={handleSubmitWarrant}
                  className="rounded-full border border-cyan-300/30 bg-cyan-400/12 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-50 hover:bg-cyan-400/18 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {solveGate?.busy
                    ? resolvedCopy.submitting
                    : isCustomSolve && solveStatus?.solutionUnlocked
                      ? resolvedCopy.warrantApproved
                      : isCustomSolve && solveStatus?.hasSubmittedWarrant
                        ? resolvedCopy.warrantRequest
                        : solveStatus?.hasSubmittedWarrant
                          ? resolvedCopy.warrantFiled
                          : hasWarrantAction
                            ? resolvedCopy.warrantRequest
                            : resolvedCopy.previewOnly}
                </button>
              </div>

              {!hasWarrantAction ? (
                <p className="mt-3 rounded-[12px] border border-white/10 bg-black/20 px-3 py-2 text-[12px] text-white/70">
                  {resolvedCopy.previewOnlyDescription}
                </p>
              ) : null}

              {solveGate?.error ? (
                <p className="mt-3 rounded-[12px] border border-red-300/20 bg-red-500/10 px-3 py-2 text-[12px] text-red-100">
                  {solveGate.error}
                </p>
              ) : null}
              {solveGate?.feedback ? (
                <p
                  className={`mt-3 rounded-[12px] px-3 py-2 text-[12px] ${
                    feedbackTone === "warning"
                      ? "border border-amber-300/20 bg-amber-500/10 text-amber-100"
                      : "border border-emerald-300/20 bg-emerald-500/10 text-emerald-100"
                  }`}
                >
                  {solveGate.feedback}
                </p>
              ) : null}

              {solveStatus?.hasSubmittedWarrant ? (
                <div className="mt-3 rounded-[12px] border border-white/10 bg-black/20 px-3 py-3 text-[12px] leading-6 text-white/82">
                  {isCustomSolve ? (
                    <p>
                      {resolvedCopy.attempts}:{" "}
                      <span className="font-semibold text-white">
                        {Math.max(1, Number(solveStatus.attemptCount || 0))}
                      </span>
                    </p>
                  ) : null}
                  <p>
                    {resolvedCopy.filedSuspect}:{" "}
                    <span className="font-semibold text-white">
                      {solveStatus.selectedSuspectName || resolvedCopy.unknownSuspect}
                    </span>
                  </p>
                  <p>
                    {resolvedCopy.result}:{" "}
                    <span className={`font-semibold ${solveStatus.isCorrect ? "text-emerald-200" : "text-amber-100"}`}>
                      {isCustomSolve
                        ? solveStatus.isCorrect
                          ? resolvedCopy.correctWarrantApproved
                          : resolvedCopy.incorrectSuspectTryAgain
                        : solveStatus.isCorrect
                          ? resolvedCopy.correctFirstWarrant
                          : resolvedCopy.solutionUnlockedWithoutSolveCredit}
                    </span>
                  </p>
                  {solveGate?.mode === "member" ? (
                    <p>
                      {resolvedCopy.recordSummary(
                        Number(solveStatus.successfulSolvesCount || 0),
                        solveStatus.nextRankLabel,
                        Number(solveStatus.solvesToNextRank || 0),
                      )}
                    </p>
                  ) : solveGate?.mode === "custom" ? (
                    <p>
                      {solveStatus.solutionUnlocked
                        ? resolvedCopy.customSolutionUnlocked
                        : resolvedCopy.customRetryPrompt}
                    </p>
                  ) : (
                    <p>{resolvedCopy.guestSessionUnlocked}</p>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          <details
            className="group mt-2.5 overflow-hidden rounded-[14px] border border-red-200/10 bg-black/20 sm:mt-4 sm:rounded-[20px]"
            open={canAccessSolution}
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-2.5 py-2.5 marker:content-none sm:px-4 sm:py-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-red-200/60">{resolvedCopy.solutionAccess}</p>
                <p className="mt-1 text-[13px] font-semibold text-white sm:text-base">
                  {resolvedCopy.openOfficialSolution}
                </p>
              </div>
              <span className="rounded-full border border-red-200/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-red-100/70 transition group-open:border-red-300/30 group-open:text-red-50 sm:px-3">
                <span className="group-open:hidden">{resolvedCopy.reveal}</span>
                <span className="hidden group-open:inline">{resolvedCopy.hide}</span>
              </span>
            </summary>

            <div className="border-t border-red-200/10 px-2 py-2 sm:px-4 sm:py-4">
              {!canAccessSolution ? (
                <div className="rounded-[14px] border border-dashed border-red-200/15 bg-white/[0.02] px-3 py-4 text-[12px] leading-6 text-white/68">
                  {isCustomSolve
                    ? customWarrantRetryable
                      ? resolvedCopy.customRetryPrompt
                      : resolvedCopy.submitCorrectPrompt
                    : resolvedCopy.submitWarrantPrompt}
                </div>
              ) : null}
              {canAccessSolution ? (
                <>
                  <div className="space-y-2 sm:space-y-3">
                    {sections.map((section, idx) => (
                      <div
                        key={`solution-${idx}`}
                        className="rounded-[14px] border border-red-200/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))] px-2.5 py-2.5 sm:rounded-[18px] sm:px-4 sm:py-4"
                      >
                        <p className="text-[10px] uppercase tracking-[0.18em] text-red-200/78 sm:text-xs">
                          {getSectionHeading(section, idx)}
                        </p>
                        <RichEvidenceText
                          value={getSectionBody(section)}
                          className={`mt-2 whitespace-pre-wrap text-white/90 sm:mt-3 ${EVIDENCE_PRIMARY_READING_CLASS}`}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mt-2.5 rounded-[14px] border border-cyan-300/18 bg-[linear-gradient(180deg,rgba(34,211,238,0.10),rgba(255,255,255,0.02))] px-2.5 py-2.5 sm:mt-4 sm:rounded-[18px] sm:px-4 sm:py-4">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-cyan-100/70">{resolvedCopy.shareTheWin}</p>
                    <p className={`mt-1 text-cyan-50/88 ${EVIDENCE_SUPPORTING_READING_CLASS}`}>
                      {resolvedCopy.shareDescription}
                    </p>
                    <div className="mt-2 rounded-[12px] border border-white/10 bg-black/20 px-2.5 py-2 sm:mt-3 sm:px-3 sm:py-2.5">
                      <p className="text-[12px] leading-5 text-white/88 sm:text-[13px] sm:leading-6">{shareText}</p>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 sm:mt-3">
                      {canNativeShare ? (
                        <button
                          type="button"
                          onClick={handleNativeShare}
                          className="rounded-full border border-cyan-300/25 bg-cyan-400/12 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-50 hover:bg-cyan-400/18"
                        >
                          {resolvedCopy.shareNow}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="rounded-full border border-white/15 bg-white/8 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/90 hover:bg-white/12"
                      >
                        {copyStatus === "copied"
                          ? resolvedCopy.copied
                          : copyStatus === "error"
                            ? resolvedCopy.copyFailed
                            : resolvedCopy.copyShareText}
                      </button>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </details>
        </div>
    </div>
  );
};

export const renderEvidenceSections = (
  evidenceFile: any,
  options: {
    suspects?: any[];
    suspectImageAccessMap?: Record<string, string>;
    caseId?: string;
    caseTitle?: string;
    evidenceTypeLabel?: string;
    finalView?: boolean;
    onOpenImage?: (url: string, alt: string) => void;
    witnessSections?: any[];
    portraitAssignmentAudit?: any[];
    copy?: EvidenceSectionHeaderCopy;
    media?: EvidenceMediaCopy;
    panels?: EvidencePanelCopy;
    solveGate?: {
      enabled?: boolean;
      mode?: "member" | "guest" | "custom";
      suspects?: any[];
      suspectImageAccessMap?: Record<string, string>;
      actorLabel?: string;
      currentRankLabel?: string;
      solveStatus?: any;
      onSubmitWarrant?: (suspectIndex: number) => Promise<void>;
      onDevResetWarrant?: () => Promise<void>;
      busy?: boolean;
      feedback?: string;
      error?: string;
      copy?: SolutionEvidenceCopy;
    };
  } = {}
) => {
  const sections = Array.isArray(evidenceFile?.sections) ? evidenceFile.sections : [];
  const images = Array.isArray(evidenceFile?.images) ? evidenceFile.images : [];
  const imagePrompt = String(evidenceFile?.image_prompt || "").trim();
  const evidenceTypeLabel =
    String(options.evidenceTypeLabel || "").trim() || humanizeEvidenceType(String(evidenceFile?.type || ""));
  const resolvedCopy = {
    eyebrow: "Agency Evidence Format",
    sections: (count: number) => `${count} section${count === 1 ? "" : "s"}`,
    images: (count: number) => `${count} image${count === 1 ? "" : "s"}`,
    ...options.copy,
  };
  const evidenceMetrics = [
    resolvedCopy.sections(sections.length),
    resolvedCopy.images(images.length),
  ];

  let content: JSX.Element;

  switch (evidenceFile?.type) {
    case "timeline":
      content = renderTimelineEvidence(sections, options.panels);
      break;
    case "victim_profile":
      content = renderVictimProfileEvidence(
        sections,
        images,
        imagePrompt,
        Array.isArray(options.suspects) ? options.suspects : [],
        options.suspectImageAccessMap || {},
        String(options.caseId || ""),
        {
          finalView: options.finalView,
          onOpenImage: options.onOpenImage,
          witnessSections: Array.isArray(options.witnessSections) ? options.witnessSections : [],
          portraitAssignmentAudit: Array.isArray(options.portraitAssignmentAudit)
            ? options.portraitAssignmentAudit
            : [],
          copy: options.media,
          panels: options.panels,
        }
      );
      break;
    case "witness_statements":
      content = renderWitnessEvidence(
        sections,
        Array.isArray(options.suspects) ? options.suspects : [],
        options.suspectImageAccessMap || {},
        {
          finalView: options.finalView,
          onOpenImage: options.onOpenImage,
          copy: options.media,
          panels: options.panels,
        }
      );
      break;
    case "suspect_texts":
      content = renderTextEvidence(sections, {
        panels: options.panels,
      });
      break;
    case "suspect_emails":
      content = renderEmailEvidence(sections, {
        panels: options.panels,
      });
      break;
    case "newspaper_articles":
      content = renderNewspaperEvidence(sections, {
        panels: options.panels,
      });
      break;
    case "police_report":
      content = renderPoliceReportEvidence(sections, {
        panels: options.panels,
      });
      break;
    case "autopsy_report":
      content = renderAutopsyEvidence(sections, {
        panels: options.panels,
      });
      break;
    case "crime_scene_photos":
      content = renderCrimeScenePhotosEvidence(sections, images, imagePrompt, {
        finalView: options.finalView,
        onOpenImage: options.onOpenImage,
        copy: options.media,
        panels: options.panels,
      });
      break;
    case "solution":
      content = (
        <SolutionEvidence
          sections={sections}
          caseTitle={String(options.caseTitle || "")}
          solveGate={options.solveGate}
        />
      );
      break;
    default:
      content = renderGenericEvidence(sections, options.panels);
  }

  return (
    <div className="space-y-4">
      <div className="rounded-[20px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] px-4 py-3 sm:rounded-[24px] sm:px-5 sm:py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#d0c296]">{resolvedCopy.eyebrow}</p>
            <p className="mt-1 text-lg font-semibold text-white sm:text-xl">{evidenceTypeLabel}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {evidenceMetrics.map((metric) => (
              <span
                key={`${evidenceTypeLabel}-${metric}`}
                className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/70"
              >
                {metric}
              </span>
            ))}
          </div>
        </div>
      </div>
      {content}
    </div>
  );
};

const renderModeToggle = (options: {
  isFinalView: boolean;
  reviewPath: string;
  finalPath: string;
  copy?: Pick<CasePreviewChromeCopy, "editReview" | "cleanFinal">;
}) => (
  <div className="inline-flex rounded-full border border-white/10 bg-black/25 p-1 shadow-[0_10px_30px_rgba(0,0,0,0.24)] backdrop-blur">
    <Link
      to={options.reviewPath}
      className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition ${
        options.isFinalView
          ? "text-white/60 hover:text-white"
          : "bg-cyan-300/18 text-cyan-50 shadow-[inset_0_0_0_1px_rgba(103,232,249,0.28)]"
      }`}
    >
      {options.copy?.editReview || "Edit Review"}
    </Link>
    <Link
      to={options.finalPath}
      className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition ${
        options.isFinalView
          ? "bg-amber-300/18 text-amber-50 shadow-[inset_0_0_0_1px_rgba(252,211,77,0.28)]"
          : "text-white/60 hover:text-white"
      }`}
    >
      {options.copy?.cleanFinal || "Clean Final"}
    </Link>
  </div>
);

const getCanonicalMemberFinalPath = (clubCase: any) => {
  const parsedCaseIndex = Number(clubCase?.caseIndex);
  if (!Number.isInteger(parsedCaseIndex) || parsedCaseIndex < 1000) return "";

  const approvalStatus = String(clubCase?.approvalStatus || "").trim().toLowerCase();
  const reviewStatus = String(clubCase?.reviewStatus || "").trim().toLowerCase();
  const approvalComplete =
    ["approved", "published_ready", "published"].includes(approvalStatus) ||
    reviewStatus === "approved";
  if (!approvalComplete || !clubCase?.isPublished) return "";

  return `${buildCasePath(
    parsedCaseIndex,
    String(clubCase?.title || ""),
    String(clubCase?.briefingTitle || "")
  )}/final`;
};

const ClubAdminCasePreviewPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { id, caseIndex, caseSlug, evidenceId } = useParams();
  const isMemberView = !id && Boolean(caseIndex);
  const isFinalView = isMemberView ? true : location.pathname.includes("/final");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draftCase, setDraftCase] = useState<any>(null);
  const [imageAccessMap, setImageAccessMap] = useState<Record<string, string>>({});
  const [suspectImageAccessMap, setSuspectImageAccessMap] = useState<Record<string, string>>({});
  const [lightboxImage, setLightboxImage] = useState<{ url: string; alt: string } | null>(null);
  const [printTheme, setPrintTheme] = useState<AgencyPrintTheme>("light");
  const [selectedPrintEvidenceIds, setSelectedPrintEvidenceIds] = useState<string[]>([]);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [pdfDownloadBusy, setPdfDownloadBusy] = useState(false);
  const [memberSolveStatus, setMemberSolveStatus] = useState<any>(null);
  const [memberRecord, setMemberRecord] = useState<any>(null);
  const [warrantBusy, setWarrantBusy] = useState(false);
  const [warrantFeedback, setWarrantFeedback] = useState("");
  const [warrantError, setWarrantError] = useState("");
  const [promotionModal, setPromotionModal] = useState<PromotionModalState>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, evidenceId]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        devLog("print", "case_surface_load_started", {
          route: location.pathname,
          isMemberView,
          isFinalView,
          caseIndex: Number(caseIndex || 0),
          caseId: String(id || ""),
        });
        const response = isMemberView
          ? await ClubApi.getCaseAccess(Number(caseIndex))
          : await ClubApi.adminGetCase(String(id));
        const match = isMemberView ? response : response?.case;
        if (!match) {
          throw new Error(isMemberView ? "Case not found" : "Generated draft not found");
        }
        setDraftCase(match);
        if (isMemberView) {
          setImageAccessMap(response?.evidenceImageAccessMap || {});
          setSuspectImageAccessMap(response?.suspectImageAccessMap || {});
          setMemberSolveStatus(response?.solveStatus || null);
        }
        devLog("print", "case_surface_load_succeeded", {
          route: location.pathname,
          isMemberView,
          isFinalView,
          title: String(match?.title || ""),
        });
      } catch (err: any) {
        devLog("print", "case_surface_load_failed", {
          route: location.pathname,
          isMemberView,
          isFinalView,
          message: String(err?.message || ""),
        });
        setError(err?.message || (isMemberView ? "Failed to load case" : "Failed to load draft preview"));
      } finally {
        setLoading(false);
      }
    };

    if (id || caseIndex) load();
  }, [caseIndex, id, isFinalView, isMemberView, location.pathname]);

  useEffect(() => {
    if (!isMemberView) {
      setMemberRecord(null);
      return;
    }

    let cancelled = false;
    devLog("print", "case_surface_member_record_load_started", {
      route: location.pathname,
      caseIndex: Number(caseIndex || 0),
    });

    ClubApi.getMe()
      .then((response) => {
        if (cancelled) return;
        const nextMemberRecord = {
          ...(response?.member || {}),
          subscription: response?.subscription || null,
        };
        setMemberRecord(nextMemberRecord);
        devLog("print", "case_surface_member_record_load_succeeded", {
          route: location.pathname,
          caseIndex: Number(caseIndex || 0),
          memberStatus: String(nextMemberRecord?.status || ""),
        });
      })
      .catch((err: any) => {
        if (cancelled) return;
        setMemberRecord(null);
        devLog("print", "case_surface_member_record_load_failed", {
          route: location.pathname,
          caseIndex: Number(caseIndex || 0),
          message: String(err?.message || ""),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [caseIndex, isMemberView, location.pathname]);

  const evidenceFiles = useMemo(
    () =>
      Array.isArray(draftCase?.casePayload?.evidence_files) ? draftCase.casePayload.evidence_files : [],
    [draftCase]
  );

  const selectedEvidenceFile = useMemo(
    () => evidenceFiles.find((file: any) => file.id === evidenceId) || null,
    [evidenceFiles, evidenceId]
  );

  const allEvidenceImageKeys = useMemo(() => {
    const keys = evidenceFiles.flatMap((file: any) =>
      getDisplayableAssetRefs(Array.isArray(file?.images) ? file.images : [])
    );
    return Array.from(new Set(keys));
  }, [evidenceFiles]);

  useEffect(() => {
    const printableEvidenceIds = evidenceFiles
      .map((file: any) => String(file?.id || "").trim())
      .filter(Boolean);
    if (!printableEvidenceIds.length) {
      setSelectedPrintEvidenceIds([]);
      return;
    }
    setSelectedPrintEvidenceIds((current) => {
      const filtered = current.filter((id) => printableEvidenceIds.includes(id));
      return filtered.length ? filtered : printableEvidenceIds;
    });
  }, [evidenceFiles]);

  useEffect(() => {
    if (isMemberView) return;
    if (!allEvidenceImageKeys.length) {
      setImageAccessMap({});
      return;
    }

    let active = true;
    const loadAccess = async () => {
      const nextMap = await resolveAssetAccessMap(allEvidenceImageKeys, async (storageKey) => {
        const response = await ClubApi.adminGetAssetAccessUrl({ storageKey });
        return String(response?.url || "");
      });
      if (!active) return;
      setImageAccessMap(nextMap);
    };

    loadAccess();
    return () => {
      active = false;
    };
  }, [allEvidenceImageKeys, isMemberView]);

  useEffect(() => {
    if (isMemberView) return;
    const suspects = Array.isArray(draftCase?.casePayload?.suspects) ? draftCase.casePayload.suspects : [];
    const storageKeys = suspects
      .map((suspect: any) => getSuspectImageRef(suspect))
      .filter(Boolean);

    if (!storageKeys.length) {
      setSuspectImageAccessMap({});
      return;
    }

    let active = true;
    const loadAccess = async () => {
      try {
        const results = await Promise.all(
          storageKeys.map(async (storageKey: string) => {
            const response = await ClubApi.adminGetAssetAccessUrl({ storageKey });
            return [storageKey, String(response?.url || "")] as const;
          })
        );
        if (!active) return;
        setSuspectImageAccessMap(Object.fromEntries(results));
      } catch {
        if (!active) return;
        setSuspectImageAccessMap(Object.fromEntries(storageKeys.map((key) => [key, ""])));
      }
    };

    loadAccess();
    return () => {
      active = false;
    };
  }, [draftCase, isMemberView]);

  const canonicalMemberFinalPath = useMemo(() => {
    if (isMemberView) return "";
    if (!isFinalView) return "";
    if (!draftCase) return "";
    return getCanonicalMemberFinalPath(draftCase);
  }, [draftCase, isFinalView, isMemberView]);

  useEffect(() => {
    if (!canonicalMemberFinalPath) return;
    navigate(canonicalMemberFinalPath, { replace: true });
  }, [canonicalMemberFinalPath, navigate]);

  const previewSuspects = Array.isArray(draftCase?.casePayload?.suspects) ? draftCase.casePayload.suspects : [];
  const previewPortraitAssignmentAudit = Array.isArray(draftCase?.generationMeta?.portraitAssignment?.audit)
    ? draftCase.generationMeta.portraitAssignment.audit
    : [];

  useEffect(() => {
    if (String(selectedEvidenceFile?.type || "").trim().toLowerCase() !== "solution") return;
    devLog("case", "solution_solve_gate_resolved", {
      route: location.pathname,
      isMemberView,
      isFinalView,
      mode: isMemberView ? "member" : "guest",
      suspectCount: previewSuspects.length,
      hasSubmitHandler: isMemberView,
      selectedSuspectIndex: Number.isInteger(Number(memberSolveStatus?.selectedSuspectIndex))
        ? Number(memberSolveStatus?.selectedSuspectIndex)
        : null,
      solutionUnlocked: Boolean(memberSolveStatus?.solutionUnlocked),
    });
  }, [
    isFinalView,
    isMemberView,
    location.pathname,
    memberSolveStatus?.selectedSuspectIndex,
    memberSolveStatus?.solutionUnlocked,
    previewSuspects.length,
    selectedEvidenceFile?.type,
  ]);

  const isStorefrontCustomSource =
    !isMemberView && String(draftCase?.generationMeta?.caseOrigin || "").trim().toLowerCase() === "storefront_custom";
  const previewLocalization = resolveCasePreviewLocalization(draftCase);
  const previewViewerLang = previewLocalization?.lang || null;
  const previewViewerCopy = previewLocalization?.copy || null;
  const previewChromeCopy = resolveCasePreviewChromeCopy({
    lang: previewViewerLang,
    isStorefrontCustomSource,
  });

  useEffect(() => {
    devLog("case", "admin_case_preview_locale_resolved", {
      route: location.pathname,
      isMemberView,
      isFinalView,
      caseOrigin: String(draftCase?.generationMeta?.caseOrigin || "").trim().toLowerCase() || "unknown",
      language: previewViewerLang || "en",
      source: previewLocalization?.source || "default_en",
      usedFallback: !previewLocalization,
    });
  }, [
    draftCase?.generationMeta?.caseOrigin,
    isFinalView,
    isMemberView,
    location.pathname,
    previewLocalization?.source,
    previewViewerLang,
  ]);

  if (loading) {
    return <div className="mx-auto max-w-7xl px-4 py-5 text-white sm:px-5 sm:py-6">Loading preview...</div>;
  }

  if (error || !draftCase) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-5 text-white sm:px-5 sm:py-6">
        <p className="text-red-400">{error || "Draft not found"}</p>
        <Link
          to={isMemberView ? "/agency/dashboard" : "/agency/admin/cases"}
          className="mt-4 inline-flex rounded-full border border-white/20 px-4 py-2 text-sm hover:bg-white/10"
        >
          {isMemberView ? "Back to Dashboard" : "Back to Admin Cases"}
        </Link>
      </div>
    );
  }

  const payload = draftCase.casePayload || {};
  const customCaseConversionPath = isStorefrontCustomSource
    ? `/agency/admin/custom-case-conversion?caseId=${encodeURIComponent(String(draftCase?._id || ""))}`
    : "";
  const newsScript = String(payload.news_script || "").trim();
  const previewDisplayName =
    String(user?.fullName || "").trim() || String(user?.username || "").trim() || "Detective";
  const previewFirstName = String(user?.firstName || "").trim() || previewDisplayName;
  const previewLastName = String(user?.lastName || "").trim();
  const previewRole = "Detective";
  const briefingNote = replacePreviewTokens(
    String(payload.briefing_body || payload.welcome_email || "").trim(),
    {
      displayName: previewDisplayName,
      firstName: previewFirstName,
      lastName: previewLastName,
      role: previewRole,
    }
  );
  const memberBasePath = `/agency/cases/${caseIndex}${caseSlug ? `/${caseSlug}` : ""}`;
  const memberCaseFilePath = `${memberBasePath}/final`;
  const memberEvidencePath = (targetEvidenceId: string) => `${memberBasePath}/evidence/${targetEvidenceId}`;
  const previewBasePath = isMemberView
    ? memberCaseFilePath
    : `/club/admin/cases/${draftCase._id}/${isFinalView ? "final" : "preview"}`;
  const reviewPacketPath = isMemberView ? memberCaseFilePath : `/club/admin/cases/${draftCase._id}/preview`;
  const finalPacketPath = isMemberView ? memberCaseFilePath : `/club/admin/cases/${draftCase._id}/final`;
  const reviewEvidencePath = evidenceId
    ? isMemberView
      ? memberEvidencePath(evidenceId)
      : `/club/admin/cases/${draftCase._id}/preview/evidence/${evidenceId}`
    : reviewPacketPath;
  const finalEvidencePath = evidenceId
    ? isMemberView
      ? memberEvidencePath(evidenceId)
      : `/club/admin/cases/${draftCase._id}/final/evidence/${evidenceId}`
    : finalPacketPath;
  const selectedEvidenceTypeLabel =
    previewViewerLang && selectedEvidenceFile?.type
      ? getLocalizedCustomCaseEvidenceTypeLabel(String(selectedEvidenceFile.type || ""), previewViewerLang)
      : getEvidenceTypeLabel(String(selectedEvidenceFile?.type || ""));

  const briefingVideoEmbedUrl = getEmbeddableVideoUrl(String(draftCase?.briefingVideoUrl || ""));
  const selectedEvidenceImageAssets = normalizeEvidenceImageRecords(selectedEvidenceFile?.images || []).map(
    (image) => ({
      storageKey: image.storageKey,
      description: image.description,
      url: imageAccessMap[image.storageKey] || String(image.url || "").trim() || "",
    })
  );
  const witnessFile = evidenceFiles.find(
    (file: any) => String(file?.type || "").trim().toLowerCase() === "witness_statements"
  );
  const witnessEvidenceSections = Array.isArray(witnessFile?.sections) ? witnessFile.sections : [];

  const printableEvidenceIds = evidenceFiles
    .map((file: any) => String(file?.id || "").trim())
    .filter(Boolean);
  const togglePrintEvidenceId = (targetEvidenceId: string) => {
    setSelectedPrintEvidenceIds((current) => {
      if (current.includes(targetEvidenceId)) {
        return current.filter((id) => id !== targetEvidenceId);
      }
      return [...current, targetEvidenceId];
    });
  };
  const handleToggleAllPrintEvidence = () => {
    setSelectedPrintEvidenceIds((current) =>
      current.length >= printableEvidenceIds.length ? [] : printableEvidenceIds
    );
  };
  const handleDownloadPdf = async () => {
    try {
      setPdfDownloadBusy(true);
      devLog("print", "case_surface_pdf_requested", {
        route: location.pathname,
        theme: printTheme,
        evidenceCount: selectedPrintEvidenceIds.length || printableEvidenceIds.length,
        isMemberView,
      });
      const response = isMemberView
        ? await ClubApi.downloadCasePdf(Number(caseIndex), {
            theme: printTheme,
            evidenceIds: selectedPrintEvidenceIds,
          })
        : await ClubApi.adminDownloadCasePdf(String(draftCase?._id || id || ""), {
            theme: printTheme,
            evidenceIds: selectedPrintEvidenceIds,
          });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = String(response.headers?.["content-disposition"] || "");
      const filenameMatch = disposition.match(/filename="([^"]+)"/i);
      link.href = url;
      link.download =
        filenameMatch?.[1] ||
        `${String(draftCase?.title || "agency-case")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "") || "agency-case"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowPrintModal(false);
      devLog("print", "case_surface_pdf_succeeded", {
        route: location.pathname,
        theme: printTheme,
        filename: link.download,
        isMemberView,
      });
    } catch (err: any) {
      devLog("print", "case_surface_pdf_failed", {
        route: location.pathname,
        theme: printTheme,
        message: String(err?.message || ""),
        isMemberView,
      });
      setError(String(err?.message || "Failed to download PDF"));
    } finally {
      setPdfDownloadBusy(false);
    }
  };
  const closePromotionModal = () => {
    if (!promotionModal) return;
    devLog("case", "member_rank_promotion_modal_closed", {
      route: location.pathname,
      caseIndex: Number(draftCase?.caseIndex || 0),
      previousRank: promotionModal.previousRankKey,
      currentRank: promotionModal.currentRankKey,
    });
    setPromotionModal(null);
  };
  const handleSubmitMemberWarrant = async (suspectIndex: number) => {
    if (!isMemberView || !draftCase?.caseIndex) return;
    try {
      setWarrantBusy(true);
      setWarrantError("");
      setWarrantFeedback("");
      devLog("case", "member_warrant_submit_requested", {
        route: location.pathname,
        caseIndex: Number(draftCase?.caseIndex || 0),
        suspectIndex,
      });
      const response = await ClubApi.submitCaseWarrant(Number(draftCase.caseIndex), { suspectIndex });
      setMemberSolveStatus(response?.solveStatus || null);
      setMemberRecord((current: any) =>
        current
          ? {
              ...current,
              rank:
                String(response?.currentRank || response?.solveStatus?.currentRankKey || "").trim() ||
                current.rank,
              successfulSolvesCount: Number(
                response?.solveStatus?.successfulSolvesCount ?? current?.successfulSolvesCount ?? 0
              ),
              warrantSubmissionsCount: Math.max(
                Number(current?.warrantSubmissionsCount || 0),
                Number(current?.warrantSubmissionsCount || 0) + 1
              ),
            }
          : current
      );
      const promotionActorLabel =
        String(user?.lastName || "").trim() ||
        String(user?.firstName || user?.fullName || "Detective").trim();
      const promotionResolution = resolveMemberRankPromotion({
        response,
        actorLabel: promotionActorLabel,
      });
      setWarrantFeedback(
        buildMemberWarrantFeedback({
          response,
          promotion: promotionResolution.promotion,
        })
      );
      devLog("case", "member_warrant_promotion_evaluated", {
        route: location.pathname,
        caseIndex: Number(draftCase?.caseIndex || 0),
        created: Boolean(response?.created),
        backendPromoted: promotionResolution.diagnostics.backendPromoted,
        inferredPromoted: promotionResolution.didPromote,
        isCorrect: promotionResolution.diagnostics.isCorrect,
        previousRank: promotionResolution.diagnostics.previousRankKey,
        currentRank: promotionResolution.diagnostics.currentRankKey,
      });
      if (response?.created === false) {
        devLog("case", "member_warrant_existing_record_detected", {
          route: location.pathname,
          caseIndex: Number(draftCase?.caseIndex || 0),
          isCorrect: Boolean(response?.warrant?.isCorrect),
          successfulSolvesCount: Number(response?.solveStatus?.successfulSolvesCount || 0),
          currentRank: String(response?.currentRank || response?.solveStatus?.currentRankKey || ""),
        });
      }
      if (promotionResolution.didPromote && promotionResolution.promotion) {
        const nextPromotion = promotionResolution.promotion;
        setPromotionModal(nextPromotion);
        devLog("case", "member_rank_promotion_modal_opened", {
          route: location.pathname,
          caseIndex: Number(draftCase?.caseIndex || 0),
          previousRank: nextPromotion.previousRankKey,
          currentRank: nextPromotion.currentRankKey,
        });
      }
      devLog("case", "member_warrant_submit_succeeded", {
        route: location.pathname,
        caseIndex: Number(draftCase?.caseIndex || 0),
        suspectIndex,
        created: Boolean(response?.created),
        isCorrect: Boolean(response?.warrant?.isCorrect),
        promoted: Boolean(response?.promoted),
        currentRank: String(response?.currentRank || response?.solveStatus?.currentRankKey || ""),
        previousRank: String(response?.previousRank || ""),
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to submit warrant.";
      setWarrantError(message);
      devLog("case", "member_warrant_submit_failed", {
        route: location.pathname,
        caseIndex: Number(draftCase?.caseIndex || 0),
        suspectIndex,
        status: err?.response?.status,
        message,
      });
    } finally {
      setWarrantBusy(false);
    }
  };
  const previewActorLabel =
    String(user?.lastName || "").trim() ||
    String(user?.firstName || user?.fullName || "Detective").trim();
  const solutionSolveGate = {
    enabled: true,
    mode: isMemberView ? ("member" as const) : ("guest" as const),
    suspects: previewSuspects,
    suspectImageAccessMap,
    actorLabel: previewActorLabel,
    currentRankLabel: isMemberView
      ? String(memberSolveStatus?.currentRankLabel || "").trim() || String(draftCase?.memberRank || "").trim()
      : "Detective",
    solveStatus: isMemberView ? memberSolveStatus : null,
    onSubmitWarrant: isMemberView ? handleSubmitMemberWarrant : undefined,
    busy: isMemberView ? warrantBusy : false,
    feedback: isMemberView ? warrantFeedback : "",
    error: isMemberView ? warrantError : "",
    copy: previewViewerCopy?.solution,
  };
  const selectedEvidenceIndex = selectedEvidenceFile
    ? evidenceFiles.findIndex((file: any) => file?.id === selectedEvidenceFile?.id)
    : -1;
  const previousEvidenceFile =
    selectedEvidenceIndex > 0 ? evidenceFiles[selectedEvidenceIndex - 1] : null;
  const nextEvidenceFile =
    selectedEvidenceIndex >= 0 && selectedEvidenceIndex < evidenceFiles.length - 1
      ? evidenceFiles[selectedEvidenceIndex + 1]
      : null;
  const previousEvidenceHref = previousEvidenceFile
    ? isMemberView
      ? memberEvidencePath(String(previousEvidenceFile.id))
      : `${previewBasePath}/evidence/${String(previousEvidenceFile.id)}`
    : "";
  const nextEvidenceHref = nextEvidenceFile
    ? isMemberView
      ? memberEvidencePath(String(nextEvidenceFile.id))
      : `${previewBasePath}/evidence/${String(nextEvidenceFile.id)}`
    : "";
  if (evidenceId && !selectedEvidenceFile) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-5 text-white sm:px-5 sm:py-6">
        <p className="text-red-400">{previewViewerCopy?.evidenceFileNotFound || "Evidence file not found."}</p>
        <div className="mt-4 flex gap-3">
          <Link
            to={previewBasePath}
            className="inline-flex rounded-full border border-white/20 px-4 py-2 text-sm hover:bg-white/10"
          >
            {previewViewerCopy?.backToCaseFile || "Back to Case File"}
          </Link>
          <Link
            to={isMemberView ? "/agency/dashboard" : "/agency/admin/cases"}
            className="inline-flex rounded-full border border-white/20 px-4 py-2 text-sm hover:bg-white/10"
          >
            {isMemberView ? "Back to Dashboard" : "Back to Admin Cases"}
          </Link>
        </div>
      </div>
    );
  }

  if (selectedEvidenceFile) {
    return (
      <section className="mx-auto max-w-[1180px] px-1.5 py-2 text-white sm:px-4 sm:py-5 md:p-7">
        {!isMemberView ? (
          <div className="sticky top-2 z-20 mb-2.5 flex justify-end sm:top-3 sm:mb-4">
            {renderModeToggle({
              isFinalView,
              reviewPath: reviewEvidencePath,
              finalPath: finalEvidencePath,
              copy: previewChromeCopy,
            })}
          </div>
        ) : null}
        <div
          className={`relative overflow-hidden rounded-[22px] p-2.5 shadow-[0_24px_70px_rgba(0,0,0,0.45)] sm:rounded-[32px] sm:p-6 ${
            isFinalView
              ? "border border-amber-200/15 bg-[radial-gradient(circle_at_top_left,rgba(120,53,15,0.28),transparent_28%),linear-gradient(135deg,#08080b,#16110a,#0f172a)]"
              : "border border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(64,95,132,0.16),transparent_24%),linear-gradient(180deg,rgba(9,12,18,0.98),rgba(7,10,16,0.98))]"
          }`}
        >
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:22px_22px] opacity-20" />
          <div className="relative flex flex-col items-start gap-2.5 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#d0c296] sm:text-xs sm:tracking-[0.28em]">
                {previewViewerCopy?.customCaseEvidence ||
                  (isMemberView
                    ? previewChromeCopy.agencyEvidenceFile
                    : isFinalView
                      ? previewChromeCopy.finalEvidenceFile
                      : previewChromeCopy.evidenceFileView)}
              </p>
              <h1 className="mt-1 text-[1.72rem] font-black leading-[1.02] text-white sm:mt-2 sm:text-3xl sm:leading-tight md:text-4xl">
                {selectedEvidenceFile.title}
              </h1>
              <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#7ed0e3] sm:mt-2 sm:text-xs sm:tracking-[0.22em]">
                {selectedEvidenceTypeLabel}
              </p>
            </div>
            <div className="flex w-full flex-wrap gap-1.5 sm:w-auto sm:gap-2">
              {!isFinalView ? (
                <Link
                  to={`/club/admin/cases/${id}/content/edit`}
                  className="rounded-full border border-emerald-300/35 bg-emerald-400/10 px-2.5 py-1.5 text-[13px] font-semibold text-emerald-100 hover:bg-emerald-400/15 sm:px-4 sm:py-2 sm:text-sm"
                >
                  {previewChromeCopy.editContent}
                </Link>
              ) : null}
              {!isFinalView ? (
                <Link
                  to={`/club/admin/cases/${id}/evidence/${evidenceId}/edit`}
                  className="rounded-full border border-amber-300/35 bg-amber-400/10 px-2.5 py-1.5 text-[13px] font-semibold text-amber-100 hover:bg-amber-400/15 sm:px-4 sm:py-2 sm:text-sm"
                >
                  {previewChromeCopy.editEvidence}
                </Link>
              ) : null}
              {customCaseConversionPath && !isFinalView ? (
                <Link
                  to={customCaseConversionPath}
                  className="rounded-full border border-fuchsia-300/35 bg-fuchsia-400/10 px-2.5 py-1.5 text-[13px] font-semibold text-fuchsia-100 hover:bg-fuchsia-400/15 sm:px-4 sm:py-2 sm:text-sm"
                >
                  {previewChromeCopy.convertToAgencyCase}
                </Link>
              ) : null}
              <Link
                to={previewBasePath}
                className="rounded-full border border-cyan-300/35 bg-cyan-400/10 px-2.5 py-1.5 text-[13px] font-semibold text-cyan-100 hover:bg-cyan-400/15 sm:px-4 sm:py-2 sm:text-sm"
              >
                {previewViewerCopy?.backToCaseFile ||
                  (isMemberView ? "Back to Case File" : isFinalView ? "Back to Final Packet" : "Back to Case File")}
              </Link>
              {!isFinalView ? (
                <Link
                  to="/club/admin/cases"
                  className="rounded-full border border-white/20 bg-white/5 px-2.5 py-1.5 text-[13px] font-semibold text-white/90 hover:bg-white/10 sm:px-4 sm:py-2 sm:text-sm"
                >
                  {previewChromeCopy.backToReview}
                </Link>
              ) : null}
            </div>
          </div>

          <div className="relative mt-3 grid gap-2 md:mt-6 md:grid-cols-4 md:gap-4">
            <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">
                {previewViewerCopy?.metadata.date || "Date"}
              </p>
              <p className="mt-1 text-[13px] text-white/85 sm:mt-1.5 sm:text-sm">
                {selectedEvidenceFile.date || previewViewerCopy?.metadata.noDate || "No date"}
              </p>
            </div>
            <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">
                {previewViewerCopy?.metadata.source || "Source"}
              </p>
              <p className="mt-1 text-[13px] text-white/85 sm:mt-1.5 sm:text-sm">
                {selectedEvidenceFile.source || previewViewerCopy?.metadata.noSource || "No source"}
              </p>
            </div>
            <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
              <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">
                {previewViewerCopy?.metadata.confidentiality || "Confidentiality"}
              </p>
              <p className="mt-1 text-[13px] text-white/85 sm:mt-1.5 sm:text-sm">
                {selectedEvidenceFile.confidentiality || previewViewerCopy?.metadata.internal || "internal"}
              </p>
            </div>
            {!isFinalView ? (
              <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
                <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">{previewChromeCopy.draftLabel}</p>
                <p className="mt-1 font-mono text-[11px] text-white/85 sm:mt-1.5 sm:text-xs">
                  {draftCase.generatedCaseId || draftCase._id}
                </p>
              </div>
            ) : (
              <div className="rounded-[16px] border border-white/10 bg-black/20 p-2 sm:rounded-2xl sm:p-4">
                <p className="text-[11px] uppercase tracking-[0.16em] text-white/45 sm:text-xs sm:tracking-wide">
                  {previewViewerCopy?.metadata.evidenceType || "Evidence Type"}
                </p>
                <p className="mt-1 text-[13px] text-white/85 sm:mt-1.5 sm:text-sm">
                  {selectedEvidenceTypeLabel}
                </p>
              </div>
            )}
          </div>

          <div
            className={`mt-3 rounded-[18px] p-2 sm:mt-6 sm:rounded-3xl sm:p-5 ${
              isFinalView
                ? "border border-amber-100/12 bg-[linear-gradient(180deg,rgba(255,248,235,0.06),rgba(255,255,255,0.02))]"
                : "border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.015))]"
            }`}
          >
            {renderEvidenceSections(
              {
                ...selectedEvidenceFile,
                images: selectedEvidenceImageAssets,
              },
              {
                suspects: previewSuspects,
                suspectImageAccessMap,
                caseId: String(id || ""),
                caseTitle: String(payload?.title || payload?.case_title || draftCase?.title || ""),
                evidenceTypeLabel: selectedEvidenceTypeLabel,
                finalView: true,
                onOpenImage: (url, alt) => setLightboxImage({ url, alt }),
                witnessSections: witnessEvidenceSections,
                portraitAssignmentAudit: previewPortraitAssignmentAudit,
                copy: previewViewerCopy
                  ? {
                      eyebrow: previewViewerCopy.customCaseEvidence,
                      sections: previewViewerCopy.sections,
                      images: previewViewerCopy.imageAssets,
                    }
                  : undefined,
                media: previewViewerCopy?.media,
                panels: previewViewerCopy?.panels,
                solveGate: solutionSolveGate,
              }
            )}
          </div>
          {renderEvidencePager({
            previousItem: previousEvidenceFile
              ? {
                  title: String(previousEvidenceFile.title || ""),
                  type: String(previousEvidenceFile.type || ""),
                  href: previousEvidenceHref,
                }
              : null,
            nextItem: nextEvidenceFile
              ? {
                  title: String(nextEvidenceFile.title || ""),
                  type: String(nextEvidenceFile.type || ""),
                  href: nextEvidenceHref,
                }
              : null,
            copy: previewViewerCopy?.pager,
            getTypeLabel: previewViewerLang
              ? (value) => getLocalizedCustomCaseEvidenceTypeLabel(value, previewViewerLang)
              : undefined,
          })}

          {!isFinalView &&
            Array.isArray(selectedEvidenceFile.clue_tags) &&
            selectedEvidenceFile.clue_tags.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wide text-white/50">{previewChromeCopy.clueTags}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedEvidenceFile.clue_tags.map((tag: string, idx: number) => (
                  <span
                    key={`${selectedEvidenceFile.id}-tag-${idx}`}
                    className="rounded-full border border-white/15 bg-white/5 px-2 py-1 text-[11px] text-white/75"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        {renderLightbox(lightboxImage, () => setLightboxImage(null))}
        <RankPromotionModal promotion={promotionModal} onClose={closePromotionModal} />
      </section>
    );
  }

  const suspects = Array.isArray(payload?.suspects) ? payload.suspects : [];
  const activeCaseIndex = Number(caseIndex || draftCase?.caseIndex || 0);
  const isFreeCase = activeCaseIndex === 1000;
  const isSimplifiedFreeCaseMemberView = isMemberView && isFreeCase;
  const isActiveCurrentMember =
    isMemberView && String(memberRecord?.status || "").trim().toLowerCase() === "active";
  const canDownloadPdf = isActiveCurrentMember && !isFreeCase;
  const caseSequenceNumber = getAgencyCaseCycleNumber(caseIndex || draftCase?.caseIndex);
  const caseSequenceLabel = `Case File ${String(caseSequenceNumber).padStart(3, "0")}`;
  const caseSummary =
    String(draftCase.summary || payload.summary || "").trim() ||
    String(payload.logline || "").trim() ||
    "A case summary has not been published yet.";
  const caseBackgroundAsset =
    evidenceFiles.find(
      (asset: any) => String(asset?.type || "").trim().toLowerCase() === "case_background"
    ) || evidenceFiles[0];
  const caseBackgroundImageUrl = caseBackgroundAsset
    ? getEvidenceThumbnailUrl(caseBackgroundAsset, imageAccessMap)
    : "";
  const caseDynamicCounts = getCaseDynamicCounts(evidenceFiles, suspects);
  const heroStatusLabel = isMemberView
    ? "Active Investigation"
    : isFinalView
      ? "Published Packet"
      : "Draft Review";
  const clearanceLabel = isMemberView ? "Member" : isFinalView ? "Final" : "Admin";
  const topDescriptor = `${caseSequenceLabel} / ${
    isMemberView ? "Internal Assignment" : isFinalView ? "Final Packet" : "Review Packet"
  }`;
  const heroChips = [
    caseDynamicCounts.evidenceCount > 0 ? "Evidence Index Available" : "Evidence Pending",
    `${caseDynamicCounts.suspectCount} Suspect Dossiers`,
    `${caseDynamicCounts.evidenceCount} Evidence Records`,
    `${caseDynamicCounts.imageCount} Image Assets`,
  ];

  return (
    <section className="mx-auto max-w-[1500px] px-3 py-4 text-white sm:px-4 sm:py-5 md:p-7">
      {!isMemberView ? (
        <div className="sticky top-3 z-20 mb-4 flex justify-end">
          {renderModeToggle({
            isFinalView,
            reviewPath: reviewPacketPath,
            finalPath: finalPacketPath,
            copy: previewChromeCopy,
          })}
        </div>
      ) : null}
      {!isMemberView ? (
        <div className="border-b border-white/8 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#7e6c44] text-sm tracking-[0.28em] text-[#dccca9]">
                C
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#9f9a8e]">Cold Case Agency</p>
                <p className="text-3xl font-semibold leading-none text-white">Case View</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isFinalView ? (
                <Link
                  to={`/agency/admin/cases/${id}/content/edit`}
                  className="rounded-full border border-emerald-300/35 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-400/15"
                >
                  {previewChromeCopy.editContent}
                </Link>
              ) : null}
              {customCaseConversionPath && !isFinalView ? (
                <Link
                  to={customCaseConversionPath}
                  className="rounded-full border border-fuchsia-300/35 bg-fuchsia-400/10 px-4 py-2 text-sm font-semibold text-fuchsia-100 hover:bg-fuchsia-400/15"
                >
                  {previewChromeCopy.convertCustomCase}
                </Link>
              ) : null}
              {!isFinalView ? (
                <Link
                  to="/agency/admin/cases"
                  className="rounded-full border border-white/12 bg-white/[0.03] px-4 py-2 text-sm font-semibold uppercase tracking-[0.14em] text-white/85 hover:bg-white/[0.08]"
                >
                  {previewChromeCopy.backToReview}
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className={`relative overflow-hidden rounded-[32px] border border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(187,140,64,0.10),transparent_24%),radial-gradient(circle_at_70%_30%,rgba(67,95,138,0.18),transparent_34%),linear-gradient(180deg,rgba(15,19,28,0.96),rgba(9,12,18,0.98))] p-7 shadow-[0_30px_90px_rgba(5,10,30,0.55)] ${isMemberView ? "mt-4" : "mt-10"}`}>
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:24px_24px] opacity-20" />
        <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
          <div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#d1c39a]">{topDescriptor}</p>
            <h1 className="mt-5 text-5xl font-black leading-[0.92] text-[#f0ede6] sm:text-6xl">
              {draftCase.title}
            </h1>
            <p className="mt-5 max-w-4xl whitespace-pre-wrap text-base leading-8 text-[#c8c4bb] sm:text-[1.05rem]">
              {caseSummary}
            </p>

            {!isSimplifiedFreeCaseMemberView ? (
              <div className="mt-10 grid gap-4 md:grid-cols-3">
                <article className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">Status</p>
                  <p className="mt-3 text-2xl font-semibold text-white">{heroStatusLabel}</p>
                </article>
                <article className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">Assigned Cycle</p>
                  <p className="mt-3 text-2xl font-semibold text-white">Cycle {caseSequenceNumber}</p>
                </article>
                <article className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#9f9a8e]">Clearance</p>
                  <p className="mt-3 text-2xl font-semibold text-white">{clearanceLabel}</p>
                </article>
              </div>
            ) : null}

            <div className="mt-6 flex flex-wrap gap-3">
              {heroChips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-[11px] uppercase tracking-[0.24em] text-[#d2cdc1]"
                >
                  {chip}
                </span>
              ))}
            </div>

            {canDownloadPdf ? (
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => {
                    devLog("print", "print_modal_opened", {
                      route: location.pathname,
                      isMemberView,
                      isFinalView,
                      theme: printTheme,
                      evidenceCount: selectedPrintEvidenceIds.length || printableEvidenceIds.length,
                    });
                    setShowPrintModal(true);
                  }}
                  className="rounded-full border border-[#d0c296]/30 bg-[#d0c296]/10 px-5 py-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#f6f0e0] transition hover:bg-[#d0c296]/18"
                >
                  Download PDF
                </button>
              </div>
            ) : null}
          </div>

	          <div className="rounded-[28px] border border-white/10 bg-[radial-gradient(circle_at_30%_20%,rgba(90,116,153,0.18),transparent_16%),linear-gradient(180deg,rgba(15,28,46,0.95),rgba(9,16,29,0.9))] p-4">
	            <div className="relative overflow-hidden rounded-[22px] border border-white/10 bg-black/30">
	              {caseBackgroundImageUrl ? (
	                <img
	                  src={caseBackgroundImageUrl}
	                  alt={`${draftCase.title} case background`}
	                  className="aspect-[16/9] w-full object-cover saturate-[0.9] contrast-[1.05] brightness-[0.86]"
	                  loading="lazy"
	                />
	              ) : (
	                <div className="aspect-[16/9] bg-[radial-gradient(circle_at_30%_20%,rgba(103,132,170,0.18),transparent_12%),linear-gradient(180deg,rgba(12,20,32,0.98),rgba(10,15,24,0.88))]" />
	              )}
	              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(3,8,18,0.08),rgba(3,8,18,0.32))]" />
	            </div>
	            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">Packet Mode</p>
                <p className="mt-2 text-lg font-semibold text-white">
                  {isMemberView ? "Member Final" : isFinalView ? "Final Preview" : "Admin Review"}
                </p>
              </div>
	              <div className="rounded-2xl border border-white/8 bg-black/20 p-4">
	                <p className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">Evidence Volume</p>
	                <p className="mt-2 text-lg font-semibold text-white">
	                  {caseDynamicCounts.sectionCount} sections
	                </p>
	              </div>
	            </div>
          </div>
        </div>
      </div>

      <div className="mt-7 grid gap-6 lg:grid-cols-[1.12fr_0.88fr]">
        <div className="space-y-6">
          <article className="rounded-[28px] border border-white/10 bg-[#090e16]/95 p-6">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-[#d0c296]">Broadcast Feed</p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight text-white">News Story</h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {draftCase.briefingVideoUrl && !isSimplifiedFreeCaseMemberView ? (
                  <a
                    href={draftCase.briefingVideoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full border border-cyan-300/25 bg-cyan-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100"
                  >
                    Open Final Video
                  </a>
                ) : null}
                {!isFinalView ? (
                  <span className="rounded-full border border-red-300/30 bg-red-500/15 px-3 py-1 text-xs font-semibold text-red-100">
                    Final Packet Preview
                  </span>
                ) : null}
              </div>
            </div>

            {draftCase.briefingVideoUrl ? (
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-black">
                {isEmbeddableVideo(draftCase.briefingVideoUrl) ? (
                  <iframe
                    src={briefingVideoEmbedUrl}
                    title="Briefing video preview"
                    className="aspect-video w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <a
                    href={draftCase.briefingVideoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex aspect-video items-center justify-center p-6 text-sm text-cyan-200 underline underline-offset-4"
                  >
                    {draftCase.briefingVideoUrl}
                  </a>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-white/20 bg-white/5 p-5 text-sm text-zinc-300">
                Add a final video URL in the content editor for this case.
              </div>
            )}

            {!isFinalView ? (
          <div className={`mt-4 rounded-[24px] border border-white/8 bg-white/[0.03] p-5 text-zinc-200 ${EVIDENCE_PRIMARY_READING_CLASS}`}>
            {newsScript || "No news script generated yet."}
          </div>
            ) : null}
          </article>

          <article className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(9,13,20,0.96))] p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-[#d0c296]">Assignment Notes</p>
                <h3 className="mt-2 text-2xl font-bold text-white">Briefing Notes</h3>
              </div>
              {!isFinalView ? (
                <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-white/75">
                  Player Copy
                </span>
              ) : null}
            </div>
            <p className={`mt-4 whitespace-pre-wrap text-[#d0d5df] ${EVIDENCE_PRIMARY_READING_CLASS}`}>
              {briefingNote || "No briefing note generated yet."}
            </p>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#9f9a8e]">Setting</p>
                <p className="mt-2 text-sm font-semibold text-white">{payload.setting?.place || "Unknown place"}</p>
                <p className="mt-1 text-sm text-white/60">{payload.setting?.time || "Unknown time"}</p>
              </div>
              <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-[#9f9a8e]">
                  {isMemberView ? "Case Status" : "Packet Status"}
                </p>
                <p className="mt-2 text-sm font-semibold text-white">
                  {String(isMemberView ? draftCase.approvalStatus || "published" : draftCase.reviewStatus || "pending").toUpperCase()}
                </p>
                <p className="mt-1 text-sm text-white/60">
                  {isMemberView
                    ? "You are viewing the published case file."
                    : isFinalView
                    ? "This route removes the admin review chrome so you can inspect the packet cleanly."
                    : "Review this page to see the packet as it currently stands before publishing."}
                </p>
              </div>
            </div>
          </article>
        </div>

        <aside className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.98),rgba(9,13,20,0.96))] p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#d0c296]">Field Archive</p>
              <h3 className="mt-2 text-2xl font-bold text-white">Evidence Index</h3>
            </div>
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-white/65">
              {isMemberView ? "Case View" : isFinalView ? "Final Look" : "Review Mode"}
            </span>
          </div>

          <div className="space-y-3">
	            {evidenceFiles.map((asset: any, index: number) => {
	              const isVictimProfile = String(asset?.type || "").trim().toLowerCase() === "victim_profile";
	              const isCaseBackground = String(asset?.type || "").trim().toLowerCase() === "case_background";
	              return (
	                <div
	                  key={asset.id || `${asset.type}-${index}`}
	                  className="rounded-[24px] border border-white/8 bg-white/[0.03] p-3"
	                >
	                  <Link
	                    to={isMemberView ? memberEvidencePath(asset.id) : `${previewBasePath}/evidence/${asset.id}`}
	                    className="block text-left transition hover:opacity-90"
	                  >
	                    <p className="text-[11px] uppercase tracking-[0.22em] text-[#9f9a8e]">
	                      {humanizeEvidenceType(asset.type)}
	                    </p>
                    <p className="mt-2 text-base font-semibold text-white">{asset.title}</p>
                    <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[#7ed0e3]">
                      Open file
                    </p>
                  </Link>
                  {isVictimProfile && suspects.length > 0 ? (
                    <div className="mt-3 border-t border-white/10 pt-3">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-amber-200/70">
                        Included suspect dossiers
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {suspects.map((suspect: any, suspectIdx: number) =>
                          isMemberView ? (
                            <span
                              key={`${asset.id}-suspect-${suspectIdx}`}
                              className="rounded-full border border-amber-300/20 bg-amber-500/10 px-2.5 py-1 text-[11px] text-amber-50/90"
                            >
                              {suspect?.name || `Suspect ${suspectIdx + 1}`}
                            </span>
                          ) : (
                            <Link
                              key={`${asset.id}-suspect-${suspectIdx}`}
                              to={`/club/admin/cases/${id}/suspect/${suspectIdx}/edit`}
                              className="rounded-full border border-amber-300/20 bg-amber-500/10 px-2.5 py-1 text-[11px] text-amber-50/90 hover:bg-amber-500/15"
                            >
                              {suspect?.name || `Suspect ${suspectIdx + 1}`}
                            </Link>
                          )
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </aside>
      </div>
      {renderLightbox(lightboxImage, () => setLightboxImage(null))}
      <RankPromotionModal promotion={promotionModal} onClose={closePromotionModal} />
      <AgencyPrintOptionsModal
        open={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        printTheme={printTheme}
        onThemeChange={setPrintTheme}
        evidenceFiles={evidenceFiles}
        selectedEvidenceIds={selectedPrintEvidenceIds}
        onEvidenceToggle={togglePrintEvidenceId}
        onToggleAllEvidence={handleToggleAllPrintEvidence}
        onDownloadPdf={handleDownloadPdf}
        pdfBusy={pdfDownloadBusy}
      />
    </section>
  );
};

export default ClubAdminCasePreviewPage;
